import { EmberInviteModal } from "./gui/ember_invite_modal"
import { WeaponAmmo } from "game-common/character/character"
import {
    ItemTypeMeta,
    locateInventoryItemObjectByItemType,
    locateInventoryItemObjects,
    resolveItemTypeToMeta,
} from "game-common/item/item"
import { SquadState } from "game-common/mechanics/combat_mechanics"
import {
    INTERACTION_REACH_TILES,
    InteractionDescriptor,
    InteractionType,
    interactionLabel,
    interactionNeedsReach,
} from "game-common/mechanics/entity_mechanics"
import {
    AbilityType,
    Coordinate,
    Dimensions,
    EmberInvasionCandidate,
    EmberInvite,
    EmberMinimapState,
    EmberPlayerEntry,
    EmberRosterEntry,
    EmberRunSummary,
    EmberTutorialPrompt,
    Entity,
    EmberPlayMode,
    EntityId,
    Inventory,
    InventoryItem,
    LIMBO_ROOM_NAME,
    LeaderboardMeta,
    ScreenDimensions,
    ScreenMessage,
    TileSize,
    TutorialType,
    WorldMapMeta,
    getDistance,
} from "game-common/models"
import { Callback, degrees_to_radians, getAngle } from "game-common/util"
import TextInput from "pixi-text-input"
import { BitmapText, Container, DisplayObject, Graphics, LINE_CAP, Loader, Point, Sprite, Text } from "pixi.js"

import { ClientGameLogic } from "../../../client_game_logic"
import { IGuiRenderingManager, KeyModifiers } from "../../../client_models"
import { isMobile, ScalingParams, sprintScaledIntent } from "../../../client_util"
import { ClientRenderable, EntityRenderingManager } from "./entity_rendering_manager"
import { AbilitiesWidget } from "./gui/abilities"
import { About } from "./gui/about"
import { Button } from "./gui/button"
import { CharacterBuilder } from "./gui/character_builder"
import { CharacterCustomizer } from "./gui/character_customizer"
import { ChatHistory, ChatLogEntry, entryFromLine } from "./gui/chat_history"
import {
    ChatHistoryWalk,
    ChatInputState,
    EMPTY_INPUT,
    EMPTY_WALK,
    EditScope,
    deleteBackward,
    deleteForward,
    historyBack,
    historyForward,
    insertText,
    moveCaret,
    rememberSent,
} from "./gui/chat_input_model"
import { CHAT_WIDTH, LOG_MAX_MESSAGES } from "./gui/chat_theme"
import { ChatChip } from "./gui/chat_chip"
import {
    ChatComposer,
    COMPOSER_PANEL_BOTTOM,
    COMPOSER_PANEL_X,
    COMPOSER_PANEL_W,
} from "./gui/chat_composer"
import { RecruitMenuHint } from "./gui/recruit_menu_hint"
import { ControlBar } from "./gui/control_bar"
import {
    CommandWheel,
    CommandWheelRole,
    HOLD_POSITION_COMMAND,
    INTERACTION_PAGE,
    isChangeRoleCommand,
} from "./gui/command_wheel"
import { LocationPicker } from "./gui/location_picker"
import { RecruitRole, RolePicker } from "./gui/role_picker"
import { NamePrompt } from "./gui/name_prompt"
import { Keyboard } from "./input/keyboard"
import { RunSummary } from "./gui/run_summary"
import { InvasionPicker, LetGoButton } from "./gui/invasion_picker"
import { PossessionSplash, PossessionSplashKind, splashForInvadingChange } from "./gui/possession_splash"
import { TutorialPrompt } from "./gui/tutorial_prompt"
import { CountdownTimer } from "./gui/countdown_timer"
import { WaveIndicator, WaveStatus } from "./gui/wave_indicator"
import { DayCycle } from "./gui/day_cycle"
import { HeroMessage } from "./gui/hero_message"
import { DynamicDialog } from "./gui/dynamic_dialog"
import { ModernInventoryDialog, isCraftingMessage, isTradeMessage } from "./gui/modern_inventory_dialog"
import { DynamicText } from "./gui/dynamic_text"
import { DyanmicTextContainer } from "./gui/dynamic_text_container"
import { AmmoReadout, EquippedWidget, TRAY_HEIGHT } from "./gui/equipped"
import { Fader } from "./gui/fader"
import { FeatureWidget } from "./gui/feature_widget"
import { Feedback } from "./gui/feedback"
import { Help } from "./gui/help"
import { IconMeter } from "./gui/icon_meter"
import { ImportantMessage } from "./gui/important_message"
import { LeaderboardWidget } from "./gui/leaderboard"
import { ManaMeter } from "./gui/mana_meter"
import { Modal, ModalProps } from "./gui/modal"
import { ResourceBar } from "./gui/resource_bar"
import { SquadWidget } from "./gui/squad"
import { EmberRosterWidget } from "./gui/ember_roster"
import { FollowCamModal } from "./gui/follow_cam"
import {
    asMinimapSize,
    clampPanelWithin,
    collectTreeCells,
    DEFAULT_MINIMAP_SIZE,
    EmberMinimapWidget,
    MinimapSize,
    PanelRect,
    rectsOverlap,
} from "./gui/ember_minimap"
import { EmberInventoryChip } from "./gui/ember_inventory_chip"
import { EmberMinimapChip } from "./gui/ember_minimap_chip"
import { EmberPlayersChip } from "./gui/ember_players_chip"
import { EmberPlayersPanel } from "./gui/ember_players_panel"
import { EmberInvitePrompt } from "./gui/ember_invite_prompt"
import { StickyBanner } from "./gui/sticky_banner"
import { Throbber } from "./gui/throbber"
import { Tutorial } from "./gui/tutorial"
import { Typing } from "./gui/typing"
import { Mouse } from "./input/mouse"
import { FloatingJoystick, FloatingSticks } from "./input/floating_sticks"
import { Intro } from "./intro/intro"
import { MinimapSurface } from "./minimap_surface"
import { getRendererApp } from "./renderer_app"
import { BasicSprite } from "./sprites/basic_sprite"
import { TextRenderer } from "./text/text_renderer"
import { WorldMap } from "./gui/world_map"

interface IncomingMessageMeta {
    unread: boolean
    flasherInterval: any
    expires: number
    text: ScreenMessage
}

export const BOTTOM = 460

// How far the HUD's two bottom corners are inset from the edges of the screen. The ember roster has
// always kept this margin on the right; the readied-item tray keeps the same one on the left, so the
// two corners are inset by the same amount rather than by two numbers that drifted apart.
export const HUD_EDGE_MARGIN = 6

// The top-right chip row: the map toggle, the inventory toggle, the players count and the chat
// button, and the gap between them. Ember rooms suppress the control bar that normally lives up
// there, so the corner is theirs.
const CHIP_ROW_Y = 6
const CHIP_GAP = 5

// Chat's ambient home, bottom-left: the input field's top edge, with the log hanging off it. The
// field's bottom has to clear the readied-item row's number labels, which start just below
// BOTTOM - 46.
const CHAT_LEFT = 15
const CHAT_INPUT_TOP = BOTTOM - 74
const CHAT_LOG_BOTTOM = CHAT_INPUT_TOP - 6

// Where the log goes while the composer is open: directly under that panel, centred on it, growing
// downward. The bottom-left corner is no use there -- it is what a soft keyboard covers, and on a
// phone it is also under the move stick.
const COMPOSER_LOG_X = COMPOSER_PANEL_X + (COMPOSER_PANEL_W - CHAT_WIDTH) / 2
const COMPOSER_LOG_TOP = COMPOSER_PANEL_BOTTOM + 8

// How much bigger the readied-item slots are under a thumb, and how far the row is lifted off the
// bottom edge to make room for the growth. See where the widget is built.
const EQUIPPED_TOUCH_SCALE = 1.5

// The tether from a per-entity command wheel to the NPC it commands. Semi-transparent so it reads as
// a link rather than another piece of HUD, and started off the rim so it doesn't cut across the disc.
const WHEEL_TETHER_ALPHA = 0.45
const WHEEL_TETHER_WIDTH = 2
const WHEEL_TETHER_RIM_GAP = 4 // how far outside the wheel's rim the line begins
const WHEEL_TETHER_MIN_LENGTH = 6 // shorter than this and the line is just a nub: don't draw it
const WHEEL_TETHER_DOT_RADIUS = 3 // marker pinning the far end to the NPC's center

// How far past its radius the wheel actually paints -- drawBackground fakes its glow with a ring at
// radius + 11 -- so a wheel parked against a screen edge is clamped on what it LOOKS like, not on
// its geometry. Hand-synced with command_wheel.ts's drawBackground.
const WHEEL_GLOW_REACH = 11

// The one slice on an interaction wheel that is not one of the entity's verbs. A card's tap belongs
// to what you can DO with the person; this is how the same tap still reaches the follow cam, which
// is otherwise only on the lord's and a partner's cards. Prefixed so it can never collide with an
// InteractionType coming off the wire.
const WATCH_SLICE = "__watch"
// How far the subject of a pinned panel may drift from that panel's tail tip before the tail stops
// telling the truth -- half a tile, i.e. they are still standing on the spot the panel opened over.
// In stage pixels, which the world overlay puts at surfaceZoom to the world unit (computeLocation).
const PANEL_TAIL_SLACK = (TileSize / 2) * ScalingParams.surfaceZoom
// The joysticks report one of eight compass sectors, and the game moves in those same eight
// directions (see Directions in game-common/models), so each sector maps to the per-axis -1/0/1
// vector the equivalent WASD combination would produce.
const JoystickVectors: Record<string, Coordinate> = {
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
    top: { x: 0, y: -1 },
    bottom: { x: 0, y: 1 },
    top_left: { x: -1, y: -1 },
    top_right: { x: 1, y: -1 },
    bottom_left: { x: -1, y: 1 },
    bottom_right: { x: 1, y: 1 },
}

// Fraction of the stick's travel that reads as "no input". The move stick used to demand 0.9,
// which left only the outermost tenth of the travel usable and let ordinary thumb jitter fall
// out of it mid-drag.
const JoystickDeadZone = 0.3

// How far out the attack stick has to be pushed to fire, held just short of the rim for the same
// reason SPRINT_POWER is: a thumb at full deflection wanders, and a gate that demanded the whole
// travel stuttered every time it wandered a pixel inwards.
const JoystickFirePower = 0.95

export class GuiManager implements IGuiRenderingManager {
    stage: Container
    minimapSurface: MinimapSurface

    // chat
    typing: Typing
    chatHistory: ChatHistory
    // The touch way into chat: a real DOM field docked at the top of the screen, opened from the
    // SAY chip / the control bar's chat button. Never up at the same time as `typing`.
    chatComposer: ChatComposer
    chatChip: ChatChip
    chatHistoryTextTimer: any
    chatHistoryFader: Fader | null = null
    chatLog: ChatLogEntry[] = []
    chatInput: ChatInputState = EMPTY_INPUT
    chatWalk: ChatHistoryWalk = EMPTY_WALK
    // Whether the input currently owns the keyboard. The log does not fade itself out while it
    // is open: a line arriving mid-sentence must not take the conversation away from the player
    // who is in the middle of answering it.
    private chatOpen: boolean = false
    private readonly CHAT_HISTORY_KEY = "_chatHistory_"

    // screen messages
    bannerText: BitmapText
    screenMessages: ScreenMessage[] = []
    currentScreenMessage: ScreenMessage | null = null
    currentMessageTimeout: any = null
    importantMessage: ImportantMessage

    // player
    // meters
    hp: IconMeter
    mana: ManaMeter
    hunger: ManaMeter
    nameText: Text

    // resource bar
    resourceBar: ResourceBar

    // leaderboard
    leaderboard: LeaderboardWidget

    // equipped
    equipped: EquippedWidget
    slotCallbacks: Record<number, Callback<void>>

    // squad
    squad: SquadWidget
    private recruitMenuHint = new RecruitMenuHint(
        () => this.logic.playerController?.suppressAttack(),
        entityId => this.recordRecruitMenuLearned(entityId, "RECRUIT_MENU_HINT_SHOWN"),
    )
    private campfireMenuHint = new RecruitMenuHint(
        () => this.logic.playerController?.suppressAttack(),
        entityId => this.recordCampfireMenuLearned(entityId, "CAMPFIRE_MENU_HINT_SHOWN"),
        "campfire",
    )
    private campfireMenuLearnedLocally = false
    private recruitHintRoster: EmberRosterEntry[] = []
    private recruitMenuLearnedLocally = false
    private recruitLessonPlayerId?: EntityId

    emberRoster: EmberRosterWidget
    followCam: FollowCamModal
    private followCamPlacement: Coordinate | null = null
    // Fade state for the follow-cam: `followCamShown` is the current target (null forces the first
    // evaluation after open to run a fade); `followCamFader` is the active alpha tween.
    private followCamShown: boolean | null = false
    private followCamFader: Fader | null = null
    // Ember-room minimap. Shares the HUD slot above the roster strip with the follow-cam PiP; the
    // PiP wins it whenever a roster member is being tracked.
    emberMinimap: EmberMinimapWidget
    // The pill the collapsed map leaves in the top-right corner, and the only way back to it.
    emberMinimapChip: EmberMinimapChip
    // The way into the pack while the control bar is suppressed, which ember rooms do -- taking the
    // usual inventory button with it. Sits in the same top-right row as the map and players toggles.
    emberInventoryChip: EmberInventoryChip
    // "Who else is playing": the count chip beside the map toggle, and the list it opens down the
    // right-hand edge. Ember-room only, and the chip only while there IS somebody else logged in.
    emberPlayersChip: EmberPlayersChip
    emberPlayersPanel: EmberPlayersPanel
    // The last list the server sent, kept so opening the panel draws the current one rather than
    // waiting for the next broadcast.
    private emberPlayers: EmberPlayerEntry[] = []
    // An invitation into somebody else's camp, awaiting this player's answer. Sits between the chip
    // and the list in the same column.
    emberInvitePrompt: EmberInvitePrompt
    // Who the outstanding invitation is from, so an answer can be addressed back to them. The prompt
    // draws the NAME; the id never has to be on screen, so it lives here rather than in the widget.
    private emberInviteFrom?: EntityId
    // Whether the player has folded the map away. Remembered across rooms and sessions: an ember run
    // drops you into a FRESH room on every death and retry, so a preference that reset each time
    // would be no preference at all.
    private emberMinimapCollapsed = false
    private readonly EMBER_MINIMAP_COLLAPSED_KEY = "_emberMinimapCollapsed_"
    // Where the player has DRAGGED the map to, in this HUD container's own coordinates, or null while
    // it is still parked in its default slot above the roster. Remembered for the same reason the
    // collapsed flag is: a run hands out a fresh room on every death and retry.
    private emberMinimapPlacement: Coordinate | null = null
    private readonly EMBER_MINIMAP_PLACEMENT_KEY = "_emberMinimapPlacement_"
    // Which of the three sizes the map is at. Remembered like the other two map preferences: a player
    // who decided the map is a radar should not have to decide it again next session.
    private emberMinimapSize: MinimapSize = DEFAULT_MINIMAP_SIZE
    private readonly EMBER_MINIMAP_SIZE_KEY = "_emberMinimapSize_"
    // Cache key for the minimap's TREE layer, which the client scans off its own tile map rather than
    // being sent: room + the map's collision version (bumped on every tile write, so a felled tree or
    // a placed wall invalidates it) + the room's extent. Null means "not scanned yet".
    private emberTreeScanKey: string | null = null
    // ...plus a floor on how often that O(tiles) scan may re-run, so a burst of tile edits (placing a
    // wall writes several cells) can't rescan on every broadcast. Low-end CPUs are the target here.
    private emberTreeScanAt = 0

    // abilities
    abilities: AbilitiesWidget

    // countdown timer
    countdownTimer: CountdownTimer

    // ember wave badge — shares the countdown dial's slot, and takes it over once the wave lands
    waveIndicator: WaveIndicator

    // command wheel
    commandWheel: CommandWheel
    private commandWheelHome = { x: 0, y: 0 }
    // The line tying a per-entity wheel back to the NPC it commands. The wheel is pinned where it
    // opened, so this is what keeps "which unit am I ordering" legible once the NPC walks away.
    private commandWheelTether: Graphics
    // Set while the wheel was opened from a ROSTER CARD rather than from the NPC itself. Such a wheel
    // is parked in the HUD slot above the strip (not over the NPC, who may well be off screen, which
    // is the whole reason the roster exists) and is tied back to its card instead of to the world.
    private commandWheelRosterId?: EntityId
    // Set alongside commandWheelRosterId when that roster wheel is carrying an ENTITY'S OWN VERBS
    // rather than squad orders (a visitor's card). Held separately because the two wheels answer a
    // selection on different channels, and because this one has to be relabelled as the player
    // walks (see refreshRosterInteractionReach).
    private rosterInteractionId?: EntityId
    private rosterInteractionName?: string
    // Whether the player was in reach of it the last time the slices were written.
    private rosterInteractionInReach = false
    // Set when the role picker was asked for from a ROSTER-opened command wheel, and therefore belongs
    // in the same HUD slot that wheel was in rather than over its member. A member reached through the
    // roster is usually one you cannot see -- and a "Change Role" order is exactly the moment it walks
    // off -- so a picker glued to the entity would open on bare ground or off screen entirely.
    private rolePickerRosterId?: EntityId
    // That line. It lives in the HUD container UNDER the roster strip, so the panel, the cards and
    // anything the strip draws over itself -- a speech bubble above all -- read over the top of it.
    private rosterWheelConnector: Graphics
    private rolePickerConnector: Graphics

    // recruit role picker (floats over a freshly-arrived recruit; assigns forager/defender)
    private emberInviteModal: EmberInviteModal
    rolePicker: RolePicker
    namePrompt: NamePrompt

    // ember end-of-run scorecard (centered; the only way back into a run after dying in the camp)
    runSummary: RunSummary

    // The invasion handshake (see gui/invasion_picker.ts): the camp-chooser the scorecard's third
    // button opens, and the small "Let go" button that exists only while wearing somebody else's
    // attacker.
    invasionPicker: InvasionPicker
    possessionSplash: PossessionSplash
    // Last invading verdict seen, so the scorecard is dismissed -- and each end of a visit
    // announced -- on the TRANSITION rather than on every message that arrives during one.
    private invading = false
    letGoButton: LetGoButton
    // The newest invasion offer off the feed. `undefined` means the feed has never spoken, which is
    // a different answer from an empty list -- see runSummaryButtons, which that distinction is the
    // whole rule of.
    private invasionCandidates?: EmberInvasionCandidate[]

    // first-run ember tutorial invitation (a low bar with show-me/skip and a draining countdown)
    tutorialPrompt: TutorialPrompt

    // hero message
    heroMessage: HeroMessage

    private currentPlayerUpgradePoints: number = 0
    private currentPlayerName: string = ""
    private currentPlayerHp: number = 0
    private currentPlayerHpMax: number = 0
    private currentPlayerHunger: number = 0
    private currentPlayerHungerMax: number = 0
    private currentPlayerMana: number = 0
    private currentScore: number

    logic: ClientGameLogic
    private currentRoomId: EntityId
    private currenRoomName?: string
    private roomNameHidden: boolean

    private leftJoystickAngleAllowed: boolean = true
    private inventoryUpdateTs: Map<EntityId, number> = new Map()

    // blackout
    blackout: Graphics

    // redout
    redout: Graphics

    private container: Graphics
    private incomingMessageMeta: IncomingMessageMeta

    private currentModal: DynamicDialog | null
    private upgradeIndicator: boolean
    private joystickSetup: boolean
    private touchSticks?: FloatingSticks
    private characterCustomizer: CharacterCustomizer
    private characterBuilder: CharacterBuilder
    private controlBar: ControlBar
    private stickyBanner: StickyBanner
    private userSuppressMinimap: boolean = false
    private tutorial: Tutorial
    private dayCycle: DayCycle
    // Per-room flag: when true, the day/night indicator stays hidden even as day-pct updates arrive.
    private suppressDayCycle: boolean = false
    // Per-room flag: when true, the "Level up stats!" button is not built in helpMessaging().
    private suppressLevelUp: boolean = false
    // The buttons the LAST helpMessaging() build put on screen. Held only so the next build can give
    // the world's mouse back on their behalf before they are removed -- see helpMessaging().
    private helpMessagingButtons: Button[] = []
    // Per-room flags: when true, the mana meter / food (hunger) meter stay hidden even as
    // update() receives new player mana/hunger values.
    private suppressMana: boolean = false
    private suppressFood: boolean = false
    // Per-room flags: when true, the shard/resource counter (resourceBar) / the player name+level
    // label stay hidden even as updateInventory()/update() would otherwise show them.
    private suppressShards: boolean = false
    private suppressPlayerName: boolean = false
    // Per-room flag: when true, a guest's standing "/register to save progress" nudge is left out of
    // the help-messaging stack. Ember rooms set it -- see Visibility.suppressGuestPrompt.
    private suppressGuestPrompt: boolean = false

    private dynamicDialogToken: string
    private featureWidget: FeatureWidget
    private mouseHoverElement: Graphics

    private loggingIn: boolean
    private intro: Intro
    private mousePointer: BasicSprite
    private locationPicker: LocationPicker
    private controlKeyPressed: boolean = false

    constructor(stage: Container, minimapSurface: MinimapSurface, logic: ClientGameLogic) {
        this.stage = stage
        this.minimapSurface = minimapSurface
        this.logic = logic
        this.incomingMessageMeta = {
            unread: false,
            flasherInterval: null,
            expires: null,
            text: null,
        }
        this.currentModal = null

        this.upgradeIndicator = false
        this.minimapSurface.register(this.mapPointHoverListener)

        // Load chat history from localStorage
        this.loadChatHistory()
    }

    init = () => {
        // meters

        const container = new Graphics()
        this.container = container
        container.sortableChildren = true
        container.x = 10
        container.zIndex = 2
        this.stage.addChild(container)

        const bannerText = new BitmapText("", {
            fontName: "BannerFont",
        })
        bannerText.y = 50
        this.bannerText = bannerText
        container.addChild(bannerText)

        container.addChild(this.minimapSurface)
        const screenWidth = ScreenDimensions.w + TileSize * 4.7

        // message — the ambient "important" strip self-positions across the full viewport,
        // so it lives on the stage (not the +10 offset HUD container) at its own zIndex.
        const importantMessage = new ImportantMessage(0xffffff, {
            text: "",
        })
        this.importantMessage = importantMessage
        this.stage.addChild(importantMessage)

        const hp = new IconMeter("heart", 0)
        hp.x = this.stage.width / 2 - hp.width
        hp.y = 2
        this.hp = hp
        container.addChild(hp)

        const mana = new ManaMeter(0xf0e54c, 19)
        mana.x = hp.x
        mana.y = hp.height + 8
        mana.visible = false
        this.mana = mana
        container.addChild(mana)

        const hunger = new ManaMeter(0xc4a484, 19, "drumstick-brown.png")
        hunger.x = mana.x
        hunger.y = mana.height + 20
        hunger.visible = false
        this.hunger = hunger
        container.addChild(hunger)

        // Chat, bottom-left: the input field, with the log hanging off its top edge. Both are the
        // same width and share a left edge, so they read as one component (gui/chat_theme.ts).
        const chatLeft = CHAT_LEFT
        const chatInputTop = CHAT_INPUT_TOP
        this.typing = new Typing()
        this.typing.x = chatLeft
        this.typing.y = chatInputTop
        this.typing.visible = false
        this.typing.zIndex = 3
        this.stage.addChild(this.typing)

        // The log sizes itself and hangs UP from this line, so a long conversation grows into the
        // empty grass above instead of pushing the field down over the hotbar.
        const chatHistory = new ChatHistory(CHAT_LOG_BOTTOM)
        chatHistory.zIndex = 3
        chatHistory.x = chatLeft
        chatHistory.y = CHAT_LOG_BOTTOM
        this.chatHistory = chatHistory
        this.stage.addChild(chatHistory)

        // The composer: the field a phone can actually type into. Above the command wheel / name
        // prompt tier (it dims the screen and takes the keyboard), below the end-of-run panels,
        // which are never up at the same time as a chat field.
        this.chatComposer = new ChatComposer({
            onSend: message => {
                this.dismissChatComposer()
                this.logic.playerController?.client?.sendChatMessage(message)
            },
            onDismiss: () => this.dismissChatComposer(),
        })
        this.chatComposer.zIndex = 60500
        this.stage.addChild(this.chatComposer)
        this.exposeChatDevTriggers()

        // leaderboard
        this.leaderboard = new LeaderboardWidget(this)
        this.leaderboard.x = ScreenDimensions.w - 25
        this.leaderboard.y = 0
        this.stage.addChild(this.leaderboard)

        // equipped
        this.equipped = new EquippedWidget(this)
        // The readied slots are the only way to swap weapon under a thumb, and at 1x they are a
        // ~32px icon on a 640x360 stage letterboxed down to a phone -- roughly 20 CSS px, and a
        // miss as often as a hit. Scaling the widget takes the slot art, the spacing and the tap
        // targets with it, the same way the control bar is scaled up on touch just below.
        // Anchored bottom-left off BOTTOM, so growing it reaches UP, into empty grass.
        if (isMobile()) {
            this.equipped.scale.set(EQUIPPED_TOUCH_SCALE)
        }
        // Inset from the LEFT edge of the screen by exactly what the ember roster is inset from the
        // right (see positionEmberRoster). Both are children of `container`, which is itself offset,
        // so the margin is measured in screen space and the parent's offset taken back out -- the old
        // `container.x + 10` was a container-local number that put the tray at 30 on screen while the
        // roster sat at 6 on the other side.
        this.equipped.x = HUD_EDGE_MARGIN - container.x
        // Anchored off the tray's KNOWN height rather than its measured one. The widget is empty
        // until the first inventory arrives, so measuring it here reads 0; the old line compensated
        // with a constant and then needed a second constant to undo what the touch scale did to the
        // guess. TRAY_HEIGHT is fixed by construction (gui/equipped.ts), so the tray can simply sit
        // on the bottom line, at either scale.
        this.equipped.y = BOTTOM - TRAY_HEIGHT * (isMobile() ? EQUIPPED_TOUCH_SCALE : 1)
        container.addChild(this.equipped)

        // squad
        this.squad = new SquadWidget(this)
        this.squad.x = 390
        this.squad.y = BOTTOM - this.squad.height - 20
        container.addChild(this.squad)

        // The connector from a roster-opened command wheel down to the card that opened it. Added
        // BEFORE the roster so the strip -- and the speech bubbles it floats above itself -- draw
        // over the line rather than the line over them.
        this.rosterWheelConnector = new Graphics()
        this.rosterWheelConnector.visible = false
        container.addChild(this.rosterWheelConnector)

        // The same line for the ROLE PICKER a roster wheel can raise. Its own Graphics rather than a
        // share of the one above: updateCommandWheelTether wipes that one on every frame the wheel is
        // closed, which is every frame the picker is up.
        this.rolePickerConnector = new Graphics()
        this.rolePickerConnector.visible = false
        container.addChild(this.rolePickerConnector)

        // ember roster (clickable NPC portraits; only populated in ember rooms). Anchored
        // bottom-right; repositioned as the card count changes (see positionEmberRoster).
        this.emberRoster = new EmberRosterWidget(this)
        container.addChild(this.emberRoster)
        this.stage.addChild(this.recruitMenuHint, this.campfireMenuHint)
        this.positionEmberRoster()

        // follow-cam PiP modal (opened by clicking a roster portrait). Positioned above the roster
        // on open (see positionFollowCam).
        this.followCam = new FollowCamModal(this, {
            onGrab: this.holdOffMinimapDragAttack,
            onDragTo: this.dragFollowCamTo,
            onRelease: this.holdOffMinimapDragAttack,
            onResize: () => this.dragFollowCamTo(this.followCam.x, this.followCam.y),
        })
        this.followCam.zIndex = 2
        container.addChild(this.followCam)

        // ember minimap — under the MAP toggle by default, and anywhere on screen once the
        // player has dragged it there (see dragEmberMinimapTo). Stays hidden until the first per-room
        // broadcast arrives, and steps aside only for a panel it actually overlaps (see
        // positionEmberMinimap). Three sizes, cycled from its own corner control.
        //
        // Every one of the three preferences — folded away, parked where, and how big — is loaded
        // BEFORE the widget goes on screen, so the map never flashes up at the default and then
        // jumps to what the player chose last session.
        this.emberMinimapCollapsed = this.loadEmberMinimapCollapsed()
        this.emberMinimapPlacement = this.loadEmberMinimapPlacement()
        this.emberMinimapSize = this.loadEmberMinimapSize()
        this.emberMinimap = new EmberMinimapWidget(
            () => this.setEmberMinimapCollapsed(true),
            {
                onGrab: this.holdOffMinimapDragAttack,
                onDragTo: this.dragEmberMinimapTo,
                onRelease: this.dropEmberMinimap,
            },
            this.onEmberMinimapResized,
        )
        this.emberMinimap.setSize(this.emberMinimapSize)
        container.addChild(this.emberMinimap)
        // The map's toggle. Unlike the panels it sits beside in the chip row, it is on screen whether
        // the map is folded or not: it is the one place that says the map HAS an open state, which a
        // control that only existed while the map was hidden could never say.
        this.emberMinimapChip = new EmberMinimapChip(this.toggleEmberMinimap)
        container.addChild(this.emberMinimapChip)

        // Same inventory the control bar's button opens; this chip is simply where it lives when
        // the room has hidden that bar.
        this.emberInventoryChip = new EmberInventoryChip(() => this.logic.invokeInventory(null))
        container.addChild(this.emberInventoryChip)

        // The chat button. Same argument as the items chip beside it: a camp suppresses the control
        // bar, and chat has only ever been opened with a key -- which a phone does not have.
        this.chatChip = new ChatChip(() => this.logic.playerController?.requestChat())
        container.addChild(this.chatChip)

        // "Who else is playing" -- the count chip sits immediately to the LEFT of the map toggle in
        // the top-right corner, and opens the list of online players down the right-hand edge below
        // it. Both stay hidden until the server says there is somebody else to show.
        this.emberPlayersPanel = new EmberPlayersPanel({
            onInvite: entityId => {
                const player = this.emberPlayers.find(entry => entry.entityId === entityId)
                if (!player || player.here) return
                this.hideCommandWheel()
                this.hideRolePicker()
                this.positionEmberInviteModal()
                this.emberInviteModal.open(entityId, player.name)
            },
            onClose: () => this.setEmberPlayersOpen(false),
        })
        this.emberInviteModal = new EmberInviteModal((entityId, playMode) => {
            const player = this.emberPlayers.find(entry => entry.entityId === entityId)
            if (!player || player.here) return
            this.inviteEmberPlayer(entityId, playMode)
            this.emberPlayersPanel.markInvited(entityId)
        })
        this.emberInviteModal.zIndex = 65000
        this.stage.addChild(this.emberInviteModal)
        container.addChild(this.emberPlayersPanel)
        this.emberPlayersChip = new EmberPlayersChip(() =>
            this.setEmberPlayersOpen(!this.emberPlayersPanel.visible),
        )
        container.addChild(this.emberPlayersChip)
        // The invitation prompt. Unlike the chip and the list this is NOT ember-only: an invitation
        // finds the player wherever they are, which is the point of being invited somewhere.
        this.emberInvitePrompt = new EmberInvitePrompt({ onAnswer: this.answerEmberInvite })
        container.addChild(this.emberInvitePrompt)

        // abilities
        this.abilities = new AbilitiesWidget(this)
        this.abilities.x = 390
        this.abilities.y = this.equipped.y
        container.addChild(this.abilities)

        // player
        this.nameText = new TextRenderer().color(0x00ff00).render() as Text
        this.nameText.y = 1
        this.nameText.x = 90
        container.addChild(this.nameText)

        // resource bar
        this.resourceBar = new ResourceBar(this)
        this.resourceBar.visible = true
        this.resourceBar.setup()
        this.resourceBar.x = this.nameText.x + 5
        this.resourceBar.y = this.nameText.y
        container.addChild(this.resourceBar)

        const blackout = new Graphics()
        blackout.x = 0
        blackout.y = 0
        blackout.lineStyle(2, 0x000000)
        blackout.beginFill(0x000000)
        blackout.drawRect(0, 0, 1000, 1000)
        blackout.visible = true
        blackout.alpha = 1.0
        blackout.endFill()
        this.blackout = blackout
        this.stage.addChild(blackout)

        const redout = new Graphics()
        redout.x = 0
        redout.y = 0
        redout.lineStyle(2, 0xff0000)
        redout.beginFill(0xff0000)
        redout.drawRect(0, 0, 1000, 1000)
        redout.visible = false
        redout.alpha = 0.3
        redout.endFill()
        this.redout = redout
        this.stage.addChild(redout)

        this.minimapSurface.x = ScreenDimensions.w + 40
        this.minimapSurface.y = BOTTOM - 105

        this.characterCustomizer = new CharacterCustomizer(this, this.logic)
        this.characterBuilder = new CharacterBuilder(this, this.logic)

        const controlBar = new ControlBar(this)
        if (isMobile()) {
            controlBar.scale.set(1.5)
        } else {
            controlBar.scale.set(0.7)
        }
        controlBar.setup(
            () => {
                this.logic.invokeInventory(null)
            },
            () => {
                this.logic.invokeQuests()
            },
            () => {
                this.invokeFeedback()
            },
            () => {
                this.logic.playerController.client.requestWorldMap()
            },
            () => {
                this.logic.playerController?.requestChat()
            },
        )

        controlBar.x = screenWidth - controlBar.width - (isMobile() ? 32 : 18)
        controlBar.y = 4
        controlBar.visible = false
        this.controlBar = controlBar
        this.container.addChild(controlBar)

        const stickyBanner = new StickyBanner(this)
        stickyBanner.setup()

        stickyBanner.y = TileSize * 0.07
        stickyBanner.x = controlBar.x - stickyBanner.width - TileSize * 0.15

        this.stickyBanner = stickyBanner
        this.container.addChild(stickyBanner)

        // The chip goes in because it is what the ember tutorial's first clue points at: a camp
        // suppresses the control bar, so the chip IS the Items button in there (see roomUpdated).
        this.tutorial = new Tutorial(
            this,
            this.container,
            this.equipped,
            this.controlBar,
            this.minimapSurface,
            this.emberInventoryChip,
        )
        this.dayCycle = new DayCycle(this)
        this.dayCycle.visible = false
        this.dayCycle.x = stickyBanner.x - TileSize * 5.25
        this.dayCycle.y = stickyBanner.y + TileSize * 0.46
        this.container.addChild(this.dayCycle)
        this.dayCycle.setup(0.25 * 360)

        this.featureWidget = new FeatureWidget(this)
        this.featureWidget.x = 0
        this.featureWidget.y = 55
        this.container.addChild(this.featureWidget)

        // countdown timer - positioned in center of screen
        this.countdownTimer = new CountdownTimer({
            radius: 50,
            strokeWidth: 7.5,
            backgroundColor: 0x222222,
            textColor: 0xffffff,
            onComplete: () => {
                // Hide timer after fade out completes (250ms + small buffer)
                setTimeout(() => {
                    this.countdownTimer.visible = false
                }, 300)
            },
        })
        // Position timer on the top-left, directly under the HP bar and left-aligned to it.
        // The HP bar lives on `container` (offset by container.x), while the timer lives on the
        // stage, so add container.x to convert into stage space. The dial is center-anchored,
        // so add half its width/height to left- and top-align it against the HP bar.
        this.countdownTimer.x = this.container.x + this.hp.x + this.countdownTimer.width / 2
        this.countdownTimer.y = this.hp.y + this.hp.height + this.countdownTimer.height / 2 + 24
        this.countdownTimer.visible = false
        this.countdownTimer.zIndex = 50000 // Lower than chat bubbles (100000)
        this.stage.addChild(this.countdownTimer)

        // Ember wave badge. Sits exactly where the countdown dial does and is the same size: the
        // dial counts DOWN to the wave, then vanishes and this takes the slot for as long as the
        // wave lasts. The two are never up together (see addScreenMessage).
        this.waveIndicator = new WaveIndicator({ radius: 50, strokeWidth: 7.5 })
        this.waveIndicator.x = this.countdownTimer.x
        this.waveIndicator.y = this.countdownTimer.y
        this.waveIndicator.zIndex = 50000
        this.stage.addChild(this.waveIndicator)

        // "Let go" -- the invader's way out, and the only exit of the five they press themself. It
        // continues the top-left column (HP bar, then the countdown dial / wave badge) rather than
        // taking a corner of its own: an invader is reading that dial, so the way out sits where
        // they are already looking. Placed once, like the dial above it.
        this.letGoButton = new LetGoButton({
            onPress: () => {
                this.logic.playerController?.client?.sendEmberInvasionCommand("release")
                this.logic.playerController?.client?.logActivity("invasion: let go")
            },
        })
        this.letGoButton.x = Math.round(this.countdownTimer.x - this.letGoButton.getWidth() / 2)
        this.letGoButton.y = Math.round(this.countdownTimer.y + 50 + 12)
        this.letGoButton.zIndex = 50001
        this.stage.addChild(this.letGoButton)

        // command wheel - positioned in center of screen
        this.commandWheel = new CommandWheel({
            radius: 80,
            strokeWidth: 3,
            backgroundColor: 0x1b1512,
            textColor: 0xfff3e0,
            highlightColor: 0xffcf87,
            activeColor: 0xff8a3d,
            onCommandSelect: (command: string, npcType: string) => {
                // Confirmation click, before the branch below: both the interaction page and the
                // squad command channel are the same gesture from the player's side.
                this.logic.audio.uiSelect()

                // targetEntityId is set only when the wheel was opened from a specific NPC's
                // command button; otherwise the command broadcasts to the whole role.
                const targetEntityId = this.commandWheel.getTargetEntityId()

                // The interaction page's "commands" are interaction types on one entity, so they go
                // out on the interaction channel rather than the squad command channel.
                if (npcType === INTERACTION_PAGE) {
                    if (command === "ember:play_mode" && targetEntityId) {
                        const mode = this.recruitHintRoster.find(entry => entry.entityId === targetEntityId)?.playMode || "pvp"
                        this.hideRolePicker()
                        this.hideCommandWheel()
                        this.rolePickerRosterId = targetEntityId
                        this.showRolePicker(targetEntityId, undefined, mode)
                        return
                    }
                    if (command === "ember:kick" && targetEntityId) {
                        this.logic.playerController.client.kickEmberPlayer(targetEntityId)
                        this.hideCommandWheel()
                        return
                    }
                    // "Watch" is the HUD's own slice, not the entity's: it swaps this wheel for the
                    // follow-cam PiP in the same slot. It only ever appears on a wheel opened from a
                    // roster card, which is the one place the follow cam is otherwise unreachable.
                    if (command === WATCH_SLICE) {
                        const watching = targetEntityId
                        const watchingName = this.rosterInteractionName
                        this.hideCommandWheel()
                        if (watching) {
                            this.openFollowCam(watching, watchingName)
                        }
                        return
                    }
                    // Out of reach: say so and leave the wheel up, rather than sending an order
                    // that the server would refuse and the player would read as a dead button. The
                    // wheel staying open matters -- "Watch" is right there, and watching them is
                    // the useful thing to do from where they are standing.
                    if (
                        targetEntityId &&
                        interactionNeedsReach(command as InteractionType) &&
                        !this.withinInteractionReach(targetEntityId)
                    ) {
                        this.addScreenMessage({
                            target: "important",
                            text: "Too far away. Go to them.",
                        })
                        return
                    }
                    this.hideCommandWheel()
                    if (targetEntityId) {
                        this.logic.interactionCallback(targetEntityId, command as InteractionType)
                        this.logic.playerController.client.logActivity(
                            `interaction wheel: ${command} on ${targetEntityId}`,
                        )
                    }
                    return
                }

                // "Hold Position" asks for a PLACE as well as an order. Hand the pointer to the
                // location picker and let the click that follows carry the spot; until then nothing
                // goes to the server. On touch there is no hovering pointer to aim with, so the
                // order keeps its older meaning there: hold the ground you are already on.
                if (command === HOLD_POSITION_COMMAND && !isMobile()) {
                    this.hideCommandWheel()
                    this.logic.playerController.beginLocationPick({
                        caption: "Click the spot to hold",
                        blockedCaption: "Nothing can stand here",
                        onPick: (point: Coordinate) => {
                            this.logic.playerController.client.sendCommandWheelSelection(
                                command,
                                npcType,
                                targetEntityId,
                                point,
                            )
                            this.logic.playerController.client.logActivity(
                                `command wheel: ${command} for ${npcType} at ` +
                                    `${Math.round(point.x)},${Math.round(point.y)}`,
                            )
                        },
                    })
                    return
                }

                // "Change Role" doesn't order anything -- it asks the server for the role picker. Where
                // that picker should OPEN is known only here, at the wheel that asked: from a roster
                // card it belongs in the HUD slot that wheel is leaving, not over a member who may be
                // off in the forest. Recorded before hideCommandWheel below lets go of the card.
                if (isChangeRoleCommand(command) && targetEntityId && this.commandWheelRosterId === targetEntityId) {
                    this.rolePickerRosterId = targetEntityId
                } else if (isChangeRoleCommand(command)) {
                    this.rolePickerRosterId = undefined
                }

                this.logic.playerController.client.sendCommandWheelSelection(command, npcType, targetEntityId)
                this.logic.playerController.client.logActivity(`command wheel: ${command} for ${npcType}`)
                // The per-entity wheel is not tied to the Control key, so dismiss it on selection.
                if (targetEntityId) {
                    this.hideCommandWheel()
                }
            },
        })
        this.commandWheel.x = screenWidth / 2
        this.commandWheel.y = ScreenDimensions.h / 2
        // Screen-center "home" the global (Control-key) wheel returns to after a per-entity wheel
        // has moved it over an NPC.
        this.commandWheelHome = { x: this.commandWheel.x, y: this.commandWheel.y }
        this.commandWheel.visible = false
        this.commandWheel.zIndex = 60000 // Higher than countdown timer
        // The tether lives in the same (stage) frame as the wheel so both ends can be expressed in
        // stage-local coordinates, and sits just under it so the line tucks behind the disc.
        this.commandWheelTether = new Graphics()
        this.commandWheelTether.visible = false
        this.commandWheelTether.zIndex = this.commandWheel.zIndex - 1
        this.stage.addChild(this.commandWheelTether)
        this.stage.addChild(this.commandWheel)

        // recruit role picker — opened by the server over a specific camp member (like the per-entity
        // command wheel). Selecting a card sends the chosen role back on the SQUAD_COMMAND channel,
        // scoped to that member, which its script turns into a swap to the role.
        //
        // Two commands, because the two moments are answered by different scripts: `recruit:<role>` is
        // the HIRE, answered by ember-recruit.js (which also charges the food), and `role:<role>` is a
        // CHANGE on an already-hired member, answered by its own role script (lib/role_change.js).
        // Keeping them apart is what stops a stray hire landing on a member, or a change on a recruit.
        this.rolePicker = new RolePicker({
            onSelect: (role: string, currentRole?: string) => {
                const targetEntityId = this.rolePicker.getTargetEntityId()
                if (role === "pvp" || role === "cooperation") {
                    this.logic.playerController.client.setEmberPlayMode(targetEntityId, role)
                    this.hideRolePicker()
                    return
                }
                const command = currentRole ? `role:${role}` : `recruit:${role}`
                this.logic.playerController.client.sendCommandWheelSelection(command, "recruit", targetEntityId)
                this.logic.playerController.client.logActivity(`role picker: ${command} for ${targetEntityId}`)
                this.hideRolePicker()
            },
        })
        this.rolePicker.visible = false
        this.rolePicker.zIndex = 60000 // Same tier as the command wheel
        this.stage.addChild(this.rolePicker)

        // name prompt -- opened by the server over a camp member (at the end of a hire, or on the
        // "Rename" order) so the player can name it. The field starts on the name the member
        // already has, so backing out is a complete answer and simply keeps it.
        this.namePrompt = new NamePrompt({
            onSubmit: (name: string) => {
                const targetEntityId = this.namePrompt.getTargetEntityId()
                if (targetEntityId) {
                    this.logic.playerController.client.sendEntityName(targetEntityId, name)
                    this.logic.playerController.client.logActivity(`named ${targetEntityId} "${name}"`)
                }
                this.hideNamePrompt()
            },
            onCancel: () => this.hideNamePrompt(),
        })
        this.namePrompt.visible = false
        this.namePrompt.zIndex = 60001 // Just over the role picker, which it opens on top of
        this.stage.addChild(this.namePrompt)

        // The location chooser — the reticle a "Hold Position" order puts under the pointer while
        // the player picks the spot. It only draws; the pick itself (and the click that ends it)
        // belongs to PlayerController, which owns world presses.
        this.locationPicker = new LocationPicker()
        this.stage.addChild(this.locationPicker)

        // ember end-of-run scorecard — put up by the server when the player dies in an ember camp
        // (Protocol.type_showRunSummary). Its two buttons go back to the ember controller, which
        // either stands the player up in the camp they fell in or resets that camp; the
        // panel dismisses itself as it sends. Whether a restart is on the table at all is the
        // camp's call (it rides in on the summary's lives) — the controller enforces it either way.
        this.runSummary = new RunSummary({
            onTryAgain: () => {
                this.logic.playerController.client.sendEmberRunCommand("tryAgain")
                this.logic.playerController.client.logActivity("run summary: try again")
                this.hideRunSummary()
            },
            onRestart: () => {
                this.logic.playerController.client.sendEmberRunCommand("restart")
                this.logic.playerController.client.logActivity("run summary: restart")
                this.hideRunSummary()
            },
            // The third way on. The press opens the picker from the list the client ALREADY holds
            // -- the offer is an interval feed, not a reply -- and the `seek` that goes with it is
            // only a nudge for a fresher list. Nothing waits on the server.
            onInvade: () => {
                this.logic.playerController.client.sendEmberInvasionCommand("seek")
                this.logic.playerController.client.logActivity("run summary: resurrect as undead")
                this.showInvasionPicker()
            },
        })
        this.runSummary.visible = false
        // Above the command wheel / role picker: this one is a full-screen modal over a finished run.
        this.runSummary.zIndex = 70000
        this.stage.addChild(this.runSummary)

        // The camp chooser, opened from the scorecard and drawn over it. Picking a card is the last
        // press of the whole handshake: the server moves the player, and every exit from there is a
        // fresh camp of their own (see docs/ember-the-fallen-design.md, "Leave").
        this.invasionPicker = new InvasionPicker({
            onJoin: (entityId: EntityId) => {
                this.logic.playerController.client.sendEmberInvasionCommand("join", entityId)
                this.logic.playerController.client.logActivity(`invasion: join ${entityId}`)
                this.hideInvasionPicker()
            },
            onCancel: () => {
                this.cancelInvasionPicker()
            },
        })
        this.invasionPicker.visible = false
        // Over the scorecard, which stays up behind it.
        this.invasionPicker.zIndex = 70001
        this.stage.addChild(this.invasionPicker)

        // The two doorways of a possession. Above BOTH of those, because it is the one thing on the
        // screen the player has not seen yet -- and because the far end of a visit arrives together
        // with a whole new room, whose load must not draw over it.
        this.possessionSplash = new PossessionSplash({
            onDismiss: () => {
                this.logic.playerController?.client?.logActivity("possession splash dismissed")
            },
        })
        this.possessionSplash.visible = false
        this.possessionSplash.zIndex = 70002
        this.stage.addChild(this.possessionSplash)

        // first-run ember tutorial invitation — put up by ember-tutorial.js on a player's very
        // first camp (Protocol.type_showTutorialPrompt). Either button answers on the generic
        // player-event channel, and so does letting the countdown run out (which means "go ahead").
        // Low on the screen and clear of the roster strip (bottom-right) and the wave dial
        // (top-left), so the Lord stays visible while he talks.
        this.tutorialPrompt = new TutorialPrompt({
            onChoose: (choice, reason) => {
                this.logic.playerController.client.sendEmberTutorialCommand(choice, reason)
                this.logic.playerController.client.logActivity(`tutorial prompt: ${choice} (${reason})`)
                this.hideTutorialPrompt()
                if (choice === "skip") {
                    // Anything still waiting on a "Next" belongs to a scene that is over, and a
                    // held bubble SWALLOWS what comes after it: lines spoken while one is up queue
                    // behind it and inherit its hold (AvatarHud.spoke), which is exactly what the
                    // Lord's sign-off must not do to the player who just declined the lesson.
                    this.logic.clientRenderer.entity().releaseHeldSpeech()
                }
            },
        })
        this.tutorialPrompt.x = screenWidth / 2 - TutorialPrompt.stageWidth() / 2
        this.anchorTutorialPrompt()
        this.tutorialPrompt.visible = false
        // Same tier as the command wheel / role picker: above the HUD, below the run scorecard.
        this.tutorialPrompt.zIndex = 60000
        this.stage.addChild(this.tutorialPrompt)

        // hero message - positioned in center of screen
        this.heroMessage = new HeroMessage()
        this.heroMessage.zIndex = 60000 // Higher than countdown timer
        this.stage.addChild(this.heroMessage)

        this.intro = new Intro(this)

        // A press anywhere that isn't the wheel puts the wheel away (see onOutsidePress). Bound once,
        // for the life of the HUD, and inert while no wheel is up.
        if (typeof document !== "undefined") {
            document.addEventListener("pointerdown", this.onOutsidePress, true)
        }
    }

    // Dismiss-on-outside-press. A wheel is modal in intent but not in fact -- it floats over the
    // world and the world goes on taking clicks underneath it -- so a press that lands anywhere else
    // has to read as "put it away". Presses arrive on the DOCUMENT (see Mouse.pressIsOnGui), which is
    // why this asks pixi what was under the pointer rather than hanging a handler off the stage: a
    // press on empty canvas hits nothing and would never reach one.
    //
    // Capture phase, so it runs before the widget the press landed on: a press on another roster card
    // closes this wheel and then that card opens its own.
    //
    // Three things own their own presses and are left alone: the wheel itself (a slice is a choice,
    // not a dismissal), the role picker (a sibling of the wheel, not a rival), and the roster strip,
    // whose cards re-target the wheel or toggle it off themselves.
    private onOutsidePress = (event: PointerEvent) => {
        if (!this.commandWheel?.isOpen()) {
            return
        }
        const target = Mouse.pressTarget(event)
        // Can't tell (no interaction plugin) is not the same as "landed on nothing": leave it up.
        if (target === undefined) {
            return
        }
        for (let node: DisplayObject | null = target; node; node = node.parent) {
            if (
                node === this.commandWheel ||
                node === this.rolePicker ||
                node === this.namePrompt ||
                node === this.emberRoster
            ) {
                return
            }
        }
        this.hideCommandWheel()
    }

    updateDayPct = (pct: number) => {
        if (pct !== undefined && !this.suppressDayCycle) {
            this.dayCycle.visible = true
            this.dayCycle.setup(pct * 360)
        }
    }

    getIntro = () => this.intro

    log = (msg: string) => this.logic.playerController.client.logActivity(msg)

    // The wire's one way into the tutorial layer: an overworld popup, an ember clue to arm, or -- on a
    // null type -- the server taking the ember clues back down (see Tutorial.setup).
    showTutorial = (tutorialType: TutorialType | null, timeoutMs?: number) => {
        this.tutorial.setup(tutorialType, timeoutMs)
    }

    // What the inventory/crafting panel calls to say where one of the clued controls landed, and to
    // take the pointer off one it is no longer drawing.
    tutorialClues = (): Tutorial => this.tutorial

    invokeFeedback = () => {
        this.logic.playerController.client.logActivity("Opened feedback widget")
        const feedback = new Feedback(this, {
            onSubmit: feedback => {
                if ((feedback.feedback || "").trim().length > 0) {
                    this.addScreenMessage({
                        target: "important",
                        text: "Thanks. Your feedback will improve the game!",
                    })
                    this.logic.playerController.client.submitFeedback(feedback)
                }
            },
        })
        feedback.setup()
    }

    helpMessaging = () => {
        // Hand the world's mouse back before anything is torn down. Both buttons below take it away
        // while the pointer is on them, and this rebuild is how they go: the message button calls
        // haltMessageIndicator from its own click handler, its throbber does the same 30s later with
        // nobody touching anything, and the level-up button goes when the points are spent. pixi
        // delivers no mouseout to a removed display object -- and none at all to one whose PARENT is
        // removed, which is how the message button goes -- so the latch would stay set and every
        // world click (walk, swing, fire) would be dead until the next room load.
        this.helpMessagingButtons.forEach(button => button.releaseMouseControl())
        this.helpMessagingButtons = []

        // cleanout old message
        const existingMessage = this.container.getChildByName("_help_messaging_")
        if (existingMessage) {
            existingMessage.parent.removeChild(existingMessage)
        }
        // The "Level up stats!" prompt is positioned independently (beside the hp/mana bars), not
        // in the help-messaging stack — clean up its previous instance here too.
        const existingLevelUp = this.container.getChildByName("_level_up_stats_")
        if (existingLevelUp) {
            existingLevelUp.parent.removeChild(existingLevelUp)
        }

        const helpMessaging = new DyanmicTextContainer()
        helpMessaging.name = "_help_messaging_"
        this.container.addChild(helpMessaging)

        const messages: string[] = []
        const items: Container[] = []

        if (this.logic.playerController.isGuest() && !this.suppressGuestPrompt) {
            messages.push("/register to save progress")
        }

        let previous = null
        messages.forEach(message => {
            const messageText = new TextRenderer().align("right").render(message)
            helpMessaging.addChild(messageText)

            if (previous) {
                messageText.y = previous.y - messageText.height
            }

            items.push(messageText)
            previous = messageText
        })

        if (this.incomingMessageMeta.unread) {
            const haltIndicator = (showDialog: boolean = true) => {
                if (showDialog) {
                    this.doDynamicDialog(this.incomingMessageMeta.text)
                }
                this.haltMessageIndicator()
            }

            const button = new Button({
                text: "Incoming message",
                font: "NameFont",
                color: 0xff0000,
                textColor: 0xffffff,
                buttonWidth: 100,
                onClick: () => {
                    this.log("open incoming message")
                    haltIndicator(true)
                },
                mouseControlCallback: (on: boolean) => {
                    this.logic.playerController.enabledMouseAction(!on)
                },
            })
            button.y = previous.y - button.height
            new Throbber(button, {
                expires: 30 * 1000,
                blinkInterval: 20,
                releaseCallback: () => haltIndicator(false),
            }).update()
            previous = button
            helpMessaging.addChild(button)
            items.push(button)
            this.helpMessagingButtons.push(button)
        }

        if (this.upgradeIndicator && !this.suppressLevelUp) {
            const levelUpPadding = 2
            const button = new Button({
                text: "Level up stats!",
                font: "NameFont",
                color: 0xfffff,
                buttonWidth: 120,
                padding: levelUpPadding,
                onClick: () => {
                    this.log("open stats upgrade ui")
                    this.logic.playerController.client.invokeStatUpgradeUI()
                },
                mouseControlCallback: (on: boolean) => {
                    this.logic.playerController.enabledMouseAction(!on)
                },
            })
            new Throbber(button, {
                expires: 5000,
                blinkInterval: 20,
            }).update()

            // Position beside (to the right of) the player's hp/mana bars. The name/level label
            // shares the top hearts row, so anchor the button's top just BELOW the hearts (at the
            // mana-bar row) and let it grow downward — otherwise it lands on the name. hp.height is
            // fresh (hp updates earlier this frame); fall back to 22 if bounds aren't ready.
            button.name = "_level_up_stats_"
            button.x = this.hp.x + this.hp.width + 12
            button.y = this.hp.y + (this.hp.height || 22) + 7
            this.container.addChild(button)
            this.helpMessagingButtons.push(button)
        }

        helpMessaging.x = this.minimapSurface.x - helpMessaging.width - 10
        helpMessaging.y = BOTTOM - 30
        items.forEach(item => {
            item.x = helpMessaging.width - item.width
        })
    }

    update = (player: Entity) => {
        if (this.currentPlayerHp !== player.hp || this.currentPlayerHpMax !== player.maxHp) {
            this.hp.max = 10
            this.hp.update((player.hp / player.maxHp) * this.hp.max)
            this.hunger.x = this.hp.x
            this.currentPlayerHp = player.hp
            this.currentPlayerHpMax = player.maxHp
        }
        if (this.currentPlayerHunger !== player.hunger || this.currentPlayerHungerMax !== player.maxHunger) {
            this.hunger.visible = !this.suppressFood
            this.hunger.update((player.hunger / player.maxHunger) * this.hunger.max)
            this.mana.x = this.hunger.x
            this.currentPlayerHunger = player.hunger
            this.currentPlayerHungerMax = player.maxHunger
        }
        if (this.currentPlayerMana !== player.mana) {
            this.mana.visible = !this.suppressMana
            this.mana.y = this.hp.height + 8
            this.mana.update((player.mana / player.maxMana) * this.mana.max)
        }
        if (this.currentPlayerName !== player.name || this.currentScore !== player.score) {
            let name = player.safeName()
            this.nameText.text = `${name}, Lvl ${Math.floor(player.level)}`
            // this.nameText.text = `${name} - Lvl ${player.level} | `
            // this.nameText.text += `${Math.floor(player.score)}/${Math.floor(player.score + player.scoreTilNextLevel)} next level`
            this.currentPlayerName = player.name
            this.currentScore = player.score
        }
        if (this.currentPlayerUpgradePoints !== player.upgradePoints) {
            this.currentPlayerUpgradePoints = player.upgradePoints
            this.upgradeIndicator = this.currentPlayerUpgradePoints > 0
            this.helpMessaging()
        }
        if (player && player.entityId === this.logic.playerEntity.entityId && !this.joystickSetup) {
            this.setupJoystick(this.container)
            this.joystickSetup = true
        }
        if (player && player.entityId === this.logic.playerEntity.entityId) {
            if (this.characterBuilder.isOpen()) {
                this.characterBuilder.update()
            }
        }
    }

    private updateRecruitMenuHint = (deltaTime: number) => {
        if (this.recruitLessonPlayerId !== this.logic.playerEntity?.entityId) {
            this.recruitLessonPlayerId = this.logic.playerEntity?.entityId
            this.recruitMenuLearnedLocally = false
            this.recruitMenuHint.resetSession()
            this.campfireMenuHint.resetSession()
            this.campfireMenuLearnedLocally = false
        }
        const hint = this.recruitMenuHint
        const blocked = this.logic.playerController?.isAttacking() ||
            ((this.recruitMenuLearnedLocally || this.logic.playerEntity?.recruitMenuLearned) && !hint.targetId) ||
            this.isCommandWheelVisible() || this.isItemScreenOpen() || this.isChatComposerVisible() ||
            this.isRolePickerVisible() || this.isNamePromptVisible() || this.tutorialPrompt?.isOpen() ||
            this.logic.playerController?.isPickingLocation()
        const candidates = this.recruitHintRoster.filter(entry =>
            entry.role === "visitor" || entry.role === "forager" || entry.role === "defender")
        const available = (id: string) => {
            const entity = this.logic.getEntity(id)
            return entity && entity.hp > 0 && !this.logic.isDestroyed(id) &&
                this.logic.clientRenderer.entity().getEntityInteractions(id)?.length
        }
        // Retain an offscreen target; replace only if it leaves the camp or disappears.
        const target = candidates.find(entry => entry.entityId === hint.targetId && available(entry.entityId)) ||
            candidates.find(entry => available(entry.entityId) &&
                this.logic.isWithinPlayerViewport(this.logic.getEntity(entry.entityId).location))
        const entity = target && this.logic.getEntity(target.entityId)
        const point = !blocked && entity && this.logic.isWithinPlayerViewport(entity.location)
            ? this.entityStageLocal(target.entityId) : null
        hint.follow(target?.entityId, point, {
            w: ScreenDimensions.w + TileSize * 4.7, h: ScreenDimensions.h,
        }, isMobile(), deltaTime)
    }

    private updateCampfireMenuHint = (deltaTime: number) => {
        const hint = this.campfireMenuHint
        const learned = this.campfireMenuLearnedLocally || this.logic.playerEntity?.campfireMenuLearned
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel || (learned && !hint.targetId)) {
            hint.visible = false
            return
        }
        const available = (entity: Entity) => entity?.contextMenuSubject === "campfire" &&
            !this.logic.isDestroyed(entity.entityId) &&
            this.logic.clientRenderer.entity().getEntityInteractions(entity.entityId)?.length
        let target = hint.targetId && this.logic.getEntity(hint.targetId)
        if (!available(target)) {
            target = undefined
            this.logic.entityManager.iterate(null, entity => {
                if (!target && available(entity) && this.logic.isWithinPlayerViewport(entity.location)) target = entity
            })
        }
        const blocked = this.recruitMenuHint.visible || this.logic.playerController?.isAttacking() ||
            this.isCommandWheelVisible() || this.isItemScreenOpen() || this.isChatComposerVisible() ||
            this.isRolePickerVisible() || this.isNamePromptVisible() || this.tutorialPrompt?.isOpen() ||
            this.logic.playerController?.isPickingLocation()
        const point = !blocked && target && this.logic.isWithinPlayerViewport(target.location)
            ? this.entityStageLocal(target.entityId) : null
        hint.follow(target?.entityId, point, {
            w: ScreenDimensions.w + TileSize * 4.7, h: ScreenDimensions.h,
        }, isMobile(), deltaTime)
    }

    private recordCampfireMenuLearned = (
        entityId: string, playerEventType: "ENTITY_CONTEXT_MENU_OPENED" | "CAMPFIRE_MENU_HINT_SHOWN",
    ) => {
        if (this.logic.getEntity(entityId)?.contextMenuSubject !== "campfire" || this.campfireMenuLearnedLocally ||
            this.logic.playerEntity?.campfireMenuLearned) return
        this.campfireMenuLearnedLocally = true
        this.logic.playerController.client.emitPlayerEvent({ playerEventType, context: { entityId } })
    }

    private learnedEntityMenu = (entityId: string) => {
        if (this.logic.getEntity(entityId)?.contextMenuSubject === "campfire") {
            this.campfireMenuHint.dismiss()
            this.recordCampfireMenuLearned(entityId, "ENTITY_CONTEXT_MENU_OPENED")
            return
        }
        this.recruitMenuHint.dismiss()
        this.recordRecruitMenuLearned(entityId, "ENTITY_CONTEXT_MENU_OPENED")
    }

    private recordRecruitMenuLearned = (
        entityId: string, playerEventType: "ENTITY_CONTEXT_MENU_OPENED" | "RECRUIT_MENU_HINT_SHOWN",
    ) => {
        const entity = this.logic.getEntity(entityId)
        if (entity?.contextMenuSubject !== "recruit") return
        if (this.recruitMenuLearnedLocally || this.logic.playerEntity?.recruitMenuLearned) return
        this.recruitMenuLearnedLocally = true
        this.logic.playerController.client.emitPlayerEvent({
            playerEventType, context: { entityId },
        })
    }

    updateTimer = (deltaTime: number) => {
        if (this.countdownTimer && this.countdownTimer.visible) {
            this.countdownTimer.update(deltaTime)
        }
        this.updateRecruitMenuHint(deltaTime)
        this.updateCampfireMenuHint(deltaTime)
        // Cheap when nothing is moving: the badge only repaints on a change or a live animation.
        this.waveIndicator?.animate(deltaTime)
        // Track the follow-cam subject each frame: hide the PiP while it's on-screen, show it when off.
        this.updateFollowCamVisibility()
        // Advance the lord's campfire-strength fire-particle backdrop.
        this.emberRoster.animate(deltaTime)
        // The character-creation screen animates its own stage (hearth glow, intro typewriter).
        this.characterBuilder.animate(deltaTime)
        // Glide the minimap's dots between snapshots (the feed only speaks once a second), and slide
        // the player's own dot, whose location is predicted locally and so needs no interpolation.
        if (this.emberMinimap?.visible) {
            this.emberMinimap.animate(deltaTime)
            this.emberMinimap.trackPlayer(this.logic.playerEntity?.location)
        }
        // Drain the players chip's arrival flash, and the transient "Sent" on an invite button. Both
        // are no-ops when nothing is running.
        this.emberPlayersChip?.animate(deltaTime)
        this.chatChip?.animate(deltaTime)
        if (this.emberPlayersPanel?.visible) {
            this.emberPlayersPanel.animate(deltaTime)
        }
        // Drain the invitation's clock. At zero the prompt takes itself down -- doing nothing is an
        // answer -- which frees the slot the list sits below.
        if (this.emberInvitePrompt?.isOpen()) {
            this.emberInvitePrompt.animate(deltaTime)
            if (!this.emberInvitePrompt.isOpen()) {
                this.emberInviteFrom = undefined
                this.positionEmberPlayers()
            }
        }
        // ...and the one behind the run scorecard's portrait, while that's up.
        if (this.emberInviteModal?.isOpen()) {
            this.positionEmberInviteModal()
        }
        this.runSummary?.animate(deltaTime)
        // Drain the invasion picker's countdowns (the feed is two seconds coarse, so the seconds
        // are counted down locally between ticks) and its fade. No-op while shut.
        this.invasionPicker?.animate(deltaTime)
        this.possessionSplash?.animate(deltaTime)
        // Drain the tutorial invitation's countdown; at zero it answers "accept" for the player.
        if (this.tutorialPrompt?.isOpen()) {
            this.tutorialPrompt.update(deltaTime)
        }
    }

    // A line a roster member has just spoken, but ONLY while the player can't see them say it —
    // once they're in the viewport their bubble is already drawn over their head in the world, and
    // repeating it on the HUD would be noise. Null otherwise. Drives the roster's speech lane.
    // A hit the player had no way of hearing: the roster saw a camp member lose health while they
    // were too far off for the world to have played anything for them.
    //
    // The predicate is deliberately the SAME one doStruck uses to decide whether to play the hit
    // sound at all (isTooDistantFromPlayer -- which also keeps the follow-cam's focus neighbourhood
    // audible), rather than a plain viewport test. Anything looser would double up the sound on a
    // member the player can already hear being hit, which is the whole reason this is conditional.
    public playOffscreenHit = (entityId: EntityId) => {
        const entity = this.logic.getEntity(entityId)
        if (entity && !this.logic.isTooDistantFromPlayer(entity)) {
            return
        }
        this.logic.audio.hit(entity)
    }

    // A stranger has reached the camp's fire. Unlike playOffscreenHit this plays wherever the
    // player is standing, including right next to them: the sound is not a substitute for seeing
    // it happen, it is the camp's doorbell, and a player watching the fire still wants to be told
    // that the thing they are watching is now asking them a question.
    public playVisitorArrival = () => {
        this.logic.audio.visitorArrives()
    }

    // Did the member behind this card FALL, or did they just stop being on the roster?
    //
    // The roster broadcast is a list, and a removal from it carries no reason -- but the two
    // reasons want opposite animations: a camp member leaves it by dying (the card greys and
    // shatters) and a VISITOR leaves it by giving up on the camp and walking back the way they came
    // (the card drifts out, alive). The entity itself is the witness, and it is still in the world
    // in the second case: a disappointed recruit walks all the way back to the map edge before it
    // self-destructs, so at the moment its card goes it is very much still standing.
    //
    // Not decided from the card's last known HP, which is a reading up to a roster tick old -- a
    // visitor cut down between two ticks would be remembered at full health and drift politely off
    // the strip instead of falling.
    public hasMemberFallen = (entityId: EntityId): boolean => {
        const entity = this.logic.getEntity(entityId)
        if (!entity || this.logic.isDestroyed(entityId)) {
            return true
        }
        return entity.isDead() || (entity.hp !== undefined && entity.hp <= 0)
    }

    public getOffscreenSpeech = (entityId: EntityId): string | null => {
        const entity = this.logic.getEntity(entityId)
        if (!entity || !entity.location || this.logic.isDestroyed(entityId)) {
            return null
        }
        if (this.logic.isWithinPlayerViewport(entity.location)) {
            return null
        }
        return this.logic.getRecentSpeech(entityId)
    }

    updateSquadState = (squadState: SquadState) => {
        this.squad.update(squadState)
    }

    // The ember camp's wave badge, driven by the wave manager script's "waveStatus" screen messages
    // (see ember-wave-manager.js). The structured counts ride in the message's context; the text
    // carries the wave number as well, so a message that somehow arrives without a context can still
    // say whether a wave is on. Wave 0 — or an unreadable payload — takes the badge down.
    private updateWaveIndicator = (message: ScreenMessage) => {
        const context = (message.context || {}) as Partial<WaveStatus>
        const wave = Number(context.wave !== undefined ? context.wave : parseInt(message.text || "0"))
        if (isNaN(wave) || wave <= 0) {
            this.waveIndicator.update(null)
            return
        }
        // A wave has landed, so the pre-wave dial has had its turn.
        if (this.countdownTimer.visible) {
            this.countdownTimer.stop()
            this.countdownTimer.visible = false
        }
        this.waveIndicator.update({
            wave,
            remaining: Number(context.remaining) || 0,
            total: Number(context.total) || 0,
            alive: Number(context.alive) || 0,
        })
    }

    updateEmberRoster = (roster: EmberRosterEntry[]) => {
        this.recruitHintRoster = roster
        this.emberRoster.update(roster)
        this.positionEmberRoster()
    }

    // Bottom-right of the HUD (the ember room suppresses the minimap that normally sits there).
    // Recomputed each update because the strip's width depends on how many cards are showing --
    // including by the widget itself, when a card finishes breaking apart between roster ticks.
    positionEmberRoster = () => {
        const screenWidth = ScreenDimensions.w + TileSize * 4.7
        const rightEdge = screenWidth - this.container.x - HUD_EDGE_MARGIN
        // Roughly aligned with the inventory-slots (equipped) widget's bottom (constant BOTTOM - 20),
        // then nudged down 15 so the whole roster/follow-cam system sits a little lower.
        const bottomEdge = BOTTOM
        this.emberRoster.x = rightEdge - this.emberRoster.rightExtent()
        this.emberRoster.y = bottomEdge - this.emberRoster.bottomExtent()
        // Keep whatever holds the slot above the roster aligned with it as cards come and go.
        this.positionFollowCam()
        this.positionRosterCommandWheel()
        this.positionRolePickerInRosterSlot()
        this.positionEmberMinimap()
    }

    // The single HUD slot directly above the ember roster strip: right-aligned with the strip, its
    // bottom edge a little clear of it. The follow-cam PiP, the minimap and a roster-opened command
    // wheel all live here (only one at a time), so they can't drift apart.
    private rosterSlotAnchor = (width: number, height: number): { x: number; y: number } => {
        const screenWidth = ScreenDimensions.w + TileSize * 4.7
        const rightEdge = screenWidth - this.container.x - HUD_EDGE_MARGIN
        const rosterTop = this.emberRoster.y - this.emberRoster.topExtent()
        return { x: rightEdge - width, y: rosterTop - 16 - height }
    }

    // Place the follow-cam below the default minimap, unless the player has dragged it elsewhere.
    private positionFollowCam = () => {
        // positionEmberRoster runs during init before followCam exists — guard against that.
        if (!this.followCam?.isOpen()) {
            return
        }
        const anchor = this.clampToScreen(
            this.followCamPlacement ?? {
                x: this.hudRightEdge() - this.followCam.getWidth(),
                y: this.chipRowBottom() + this.emberMinimap.getDefaultHeight() + CHIP_GAP,
            },
            this.followCam.getWidth(),
            this.followCam.getHeight(),
        )
        this.followCam.x = anchor.x
        this.followCam.y = anchor.y
        this.updateFollowCamVisibility()
    }

    private dragFollowCamTo = (x: number, y: number) => {
        this.holdOffMinimapDragAttack()
        this.followCamPlacement = this.clampToScreen({ x, y }, this.followCam.getWidth(), this.followCam.getHeight())
        this.followCam.x = this.followCamPlacement.x
        this.followCam.y = this.followCamPlacement.y
    }

    // Where the minimap lives until the player drags it somewhere else: directly under the MAP toggle
    // that turns it on and off, hard against the same right edge, so the control and the thing it
    // controls are one object in the corner.
    //
    // It used to default to the slot above the roster strip -- a 176px map wants somewhere roomy -- but
    // a radar wants to be next to its switch, and that slot is the follow-cam PiP's anyway. The chip's
    // row is measured rather than assumed because the chips carry text, whose metrics are the font's
    // business.
    private minimapDefaultAnchor = (width: number): Coordinate => ({
        x: this.hudRightEdge() - width,
        y: this.chipRowBottom(),
    })

    // The y directly below the top-right chip row. Reserved whether or not any chip is currently IN
    // it, for the same reason layoutChipRow reserves each chip's slot: nothing stacked underneath
    // should slide about as chips come and go.
    private chipRowBottom = (): number => CHIP_ROW_Y + this.emberMinimapChip.getHeight() + CHIP_GAP

    // The command wheel and role picker temporarily hide an overlapping map. The follow-cam
    // can overlap it freely and renders above it instead.
    private claimedSlotRect = (): PanelRect | null => {
        const rect = (width: number, height: number): PanelRect => {
            const at = this.clampToScreen(this.rosterSlotAnchor(width, height), width, height)
            return { x: at.x, y: at.y, width, height }
        }
        if (this.commandWheelRosterId && this.commandWheel) {
            const span = 2 * (this.commandWheel.getRadius() + WHEEL_GLOW_REACH)
            return rect(span, span)
        }
        if (this.rolePickerRosterId && this.rolePicker) {
            return rect(this.rolePicker.getPanelWidth(), this.rolePicker.getPanelHeight())
        }
        return null
    }

    // The minimap's home is under its own MAP toggle (see minimapDefaultAnchor), and anywhere the
    // player has dragged it to after that.
    //
    // Wherever it is, it steps aside for whatever has claimed the slot above the roster strip — the
    // roster command wheel or role picker — but ONLY if the two would actually
    // OVERLAP. That last part matters more than it sounds: the map used to vanish for any of those
    // panels wherever it had been dragged to, so a player who had parked it in the opposite corner
    // watched it disappear every time they tapped a roster card, for no reason they could see — which
    // is how you come away from a playthrough not realising the map can simply be left open. A panel
    // the map is nowhere near is not a reason to take the map away.
    //
    // Collapsed, the map gives up its space entirely and its chip carries the "off" state instead. The
    // chip stays on screen either way, including while another panel overlaps the map, so the
    // player can always change whether the map is enabled.
    private positionEmberMinimap = () => {
        if (!this.emberMinimap) {
            return
        }
        const hasMap = this.emberMinimap.hasData()
        const width = this.emberMinimap.getWidth()
        const height = this.emberMinimap.getHeight()
        // Re-clamped rather than taken as read: the map is a plan of the room and carries its aspect
        // ratio, and it has three sizes — so the panel a placement was chosen for may not be the size
        // of this one.
        const at = this.emberMinimapPlacement
            ? this.clampToScreen(this.emberMinimapPlacement, width, height)
            : this.minimapDefaultAnchor(width)
        const claimed = this.claimedSlotRect()
        const clear = !claimed || !rectsOverlap({ x: at.x, y: at.y, width, height }, claimed)

        const show = hasMap && clear && !this.emberMinimapCollapsed
        this.emberMinimap.visible = show
        if (show) {
            this.emberMinimap.x = at.x
            this.emberMinimap.y = at.y
        }

        this.emberMinimapChip.visible = hasMap
        this.emberMinimapChip.setActive(!this.emberMinimapCollapsed)
        // Top-right corner of the screen. Ember rooms suppress the control bar that normally lives
        // there, so the corner is the camp HUD's. Laid out unconditionally: the map toggle holds its
        // slot even while it is hidden, so the chips beside it do not move when the map is unfolded.
        this.layoutChipRow()
        // The map now sits at the head of the right-edge column, so whatever stacks under it has to be
        // told when the map appears, folds away, or changes size. One-way: positionEmberPlayers reads
        // the map's placement and never sets it.
        if (this.emberPlayersChip) {
            this.positionEmberPlayers()
        }
    }

    // The x every right-justified HUD panel measures back from, in this container's coordinates.
    private hudRightEdge = (): number => ScreenDimensions.w + TileSize * 4.7 - this.container.x - 6

    // The top-right chip row, laid out right to left: map, items, say, players.
    //
    // Every chip keeps its slot whether or not it is currently on screen. They appear and vanish
    // independently -- the map toggle exists while the room has map data, the players count
    // only while somebody else is logged in -- and a row that closed up around a missing one would
    // shuffle the others sideways under the player's thumb each time that happened. Reserving the
    // slots costs a few empty pixels and buys a row that never moves.
    //
    // Called again whenever a chip's width can change; the players chip carries a count, so it
    // does.
    private layoutChipRow = () => {
        let right = this.hudRightEdge()
        const place = (chip?: { x: number; y: number; getWidth: () => number }) => {
            if (!chip) {
                return
            }
            chip.x = right - chip.getWidth()
            chip.y = CHIP_ROW_Y
            right -= chip.getWidth() + CHIP_GAP
        }
        place(this.emberMinimapChip)
        place(this.emberInventoryChip)
        // Inboard of the players chip on purpose: that one carries a count, so its width moves when
        // a tenth player logs in -- and everything LEFT of it moves with it. Reserving slots keeps
        // the row still as chips come and go, but it cannot keep a chip still beside one that
        // grows, so the growing one goes on the end.
        place(this.chatChip)
        place(this.emberPlayersChip)
    }

    // Who else is logged in, as the server last told us. An empty list takes the chip down and, with
    // it, the panel -- there is nothing left to invite.
    updateEmberPlayers = (players: EmberPlayerEntry[]) => {
        if (!this.emberPlayersChip) {
            // A room update can land before the HUD has finished standing itself up.
            return
        }
        this.emberPlayers = players || []
        // The chip owns its own visibility (and the orange flash it makes on arrival) -- it is the
        // count that decides, and only the chip knows whether this count is the first one.
        this.emberPlayersChip.update(this.emberPlayers.length)
        if (this.emberPlayers.length < 1) {
            this.emberPlayersPanel.visible = false
        }
        if (this.emberPlayersPanel.visible) {
            this.emberPlayersPanel.update(this.emberPlayers)
        }
        this.positionEmberPlayers()
    }

    // Open or close the player list. Filled on open rather than kept up to date while hidden: the feed
    // only speaks when the list CHANGES, so a panel that waited for the next broadcast could sit empty
    // indefinitely.
    private setEmberPlayersOpen = (open: boolean) => {
        const show = open && this.emberPlayers.length > 0
        if (show) {
            this.emberPlayersPanel.update(this.emberPlayers)
        }
        this.emberPlayersPanel.visible = show
        this.positionEmberPlayers()
    }

    // Ask the server to pull another player into this camp. It is a request in both directions: the
    // server decides whether it is allowed and where they land, and the INVITED player decides
    // whether to come at all (see the EMBER_PLAYERS campaign and updateEmberInvite).
    private positionEmberInviteModal = () => {
        const screen = getRendererApp().renderer.screen
        // Renderer coordinates include the stage's scale and translation. Convert both viewport
        // corners into the modal's space before drawing the scrim or positioning the dialog.
        const topLeft = this.emberInviteModal.toLocal(new Point(screen.x, screen.y))
        const bottomRight = this.emberInviteModal.toLocal(new Point(screen.x + screen.width, screen.y + screen.height))
        this.emberInviteModal.resize(bottomRight.x - topLeft.x, bottomRight.y - topLeft.y, topLeft.x, topLeft.y)
    }

    private inviteEmberPlayer = (entityId: EntityId, playMode: EmberPlayMode = "pvp") => {
        this.logic.playerController?.client?.inviteEmberPlayer(entityId, playMode)
    }

    // An invitation into somebody's camp, or null to take the prompt down. Deliberately NOT gated on
    // being in an ember room: the player being invited is, by definition, somewhere else.
    updateEmberInvite = (invite: EmberInvite | null) => {
        if (!this.emberInvitePrompt) {
            return
        }
        this.emberInviteFrom = invite?.fromEntityId
        this.emberInvitePrompt.update(invite)
        this.positionEmberPlayers()
    }

    // The player's answer. Only an accepted one moves them, and the server does the moving -- this
    // says nothing about where they will land.
    private answerEmberInvite = (accepted: boolean) => {
        const from = this.emberInviteFrom
        this.emberInviteFrom = undefined
        if (from) {
            this.logic.playerController?.client?.answerEmberInvite(from, accepted)
        }
        this.positionEmberPlayers()
    }

    // The players chip takes the leftmost place in the top-right row (see layoutChipRow), and the
    // list of players hangs under it against the same right edge.
    private positionEmberPlayers = () => {
        if (!this.emberPlayersChip) {
            return
        }
        const rightEdge = this.hudRightEdge()
        // The chip's own place in the top-right row. Re-run here because this chip's width moves
        // with the count it carries, which nothing else is watching.
        this.layoutChipRow()
        // One column down the right edge: the chip's row, then the MAP if it is still parked under its
        // toggle, then the invitation prompt, then the list. The chip's row is reserved whether or not
        // the chip is IN it, for the same reason the map toggle's slot is -- so nothing below it slides
        // about as players come and go.
        let below = this.chipRowBottom()
        if (this.minimapHoldsTheCorner()) {
            below += this.emberMinimap.getHeight() + CHIP_GAP
        }
        if (this.emberInvitePrompt?.isOpen()) {
            this.emberInvitePrompt.x = rightEdge - this.emberInvitePrompt.getWidth()
            this.emberInvitePrompt.y = below
            below += this.emberInvitePrompt.getHeight() + CHIP_GAP
        }
        this.emberPlayersPanel.x = rightEdge - this.emberPlayersPanel.getWidth()
        this.emberPlayersPanel.y = below
    }

    // Whether the map is currently sitting in its default spot under the MAP toggle -- in which case
    // everything else that stacks down that right edge has to start below it rather than under the
    // chip row. Derived rather than remembered, so it cannot be read before positionEmberMinimap has
    // run: the two only share the chip row's height and the map's own size.
    private minimapHoldsTheCorner = (): boolean =>
        !!this.emberMinimap?.visible && !this.emberMinimapPlacement

    // What the top-right MAP chip does: flip the map between shown and folded away. One control for
    // both directions, because that is what makes it read as a toggle with a state rather than a
    // button that opens something.
    private toggleEmberMinimap = () => this.setEmberMinimapCollapsed(!this.emberMinimapCollapsed)

    // Fold the map away (or bring it back), remembering the choice.
    private setEmberMinimapCollapsed = (collapsed: boolean) => {
        if (this.emberMinimapCollapsed === collapsed) {
            return
        }
        this.emberMinimapCollapsed = collapsed
        this.saveEmberMinimapCollapsed(collapsed)
        this.positionEmberMinimap()
    }

    // The collapsed preference is a per-viewer convenience, so it lives in localStorage — which can
    // throw or come back empty (private windows, blocked site data), hence the guards.
    private loadEmberMinimapCollapsed = (): boolean => {
        try {
            if (typeof localStorage !== "undefined") {
                return localStorage.getItem(this.EMBER_MINIMAP_COLLAPSED_KEY) === "true"
            }
        } catch (error) {
            console.warn("Failed to load minimap collapsed state from localStorage:", error)
        }
        return false
    }

    private saveEmberMinimapCollapsed = (collapsed: boolean) => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.EMBER_MINIMAP_COLLAPSED_KEY, String(collapsed))
            }
        } catch (error) {
            console.warn("Failed to save minimap collapsed state to localStorage:", error)
        }
    }

    // The widget owns the size cycle and tells us where it landed. Two jobs here: remember it, and
    // re-place a panel that has just changed size under a placement chosen for the old one.
    private onEmberMinimapResized = (size: MinimapSize) => {
        this.emberMinimapSize = size
        this.saveEmberMinimapSize(size)
        this.positionEmberMinimap()
    }

    // Like the other two map preferences, a per-viewer convenience in localStorage — which can throw
    // or come back empty (private windows, blocked site data), and whose contents are whatever was
    // last written there, hence the guards and asMinimapSize.
    private loadEmberMinimapSize = (): MinimapSize => {
        try {
            if (typeof localStorage !== "undefined") {
                return asMinimapSize(localStorage.getItem(this.EMBER_MINIMAP_SIZE_KEY)) ?? DEFAULT_MINIMAP_SIZE
            }
        } catch (error) {
            console.warn("Failed to load minimap size from localStorage:", error)
        }
        return DEFAULT_MINIMAP_SIZE
    }

    private saveEmberMinimapSize = (size: MinimapSize) => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.EMBER_MINIMAP_SIZE_KEY, size)
            }
        } catch (error) {
            console.warn("Failed to save minimap size to localStorage:", error)
        }
    }

    // ---- dragging the minimap ------------------------------------------------
    //
    // The whole panel is a grab handle (see EmberMinimapWidget.attachDrag), because the part of the
    // camp the map covers is sometimes the part the player needs to watch. GuiManager does the moving
    // rather than the widget: it is what re-anchors the panel above the roster strip, and so the only
    // place that can both honour a dragged position and keep it on screen.

    // A press on the panel must never also swing the player's axe. The attack click arrives via a
    // document-level mousedown that cannot see which pixi widget it landed on, so it is held off the
    // same way an interaction-icon click is (see Player.suppressAttack) — renewed on grab, on every
    // drag frame and once more on release, so a drag of any length is covered end to end. A drag that
    // pauses needs no cover: a still pointer raises no mouse events, so nothing can fire.
    private static readonly MINIMAP_DRAG_ATTACK_HOLDOFF_MS = 500
    private holdOffMinimapDragAttack = () =>
        this.logic.playerController?.suppressAttack(GuiManager.MINIMAP_DRAG_ATTACK_HOLDOFF_MS)

    private dragEmberMinimapTo = (x: number, y: number) => {
        this.holdOffMinimapDragAttack()
        const at = this.clampToScreen({ x, y }, this.emberMinimap.getWidth(), this.emberMinimap.getHeight())
        this.emberMinimapPlacement = at
        this.emberMinimap.x = at.x
        this.emberMinimap.y = at.y
    }

    // Saved on release rather than per frame: a drag is hundreds of positions and only the last one is
    // the answer.
    private dropEmberMinimap = () => {
        this.holdOffMinimapDragAttack()
        this.saveEmberMinimapPlacement()
    }

    // The screen, in the HUD container's own coordinates: the container is nudged in from the left
    // edge, so screen left sits at -container.x; the right edge is the same expression the rest of the
    // HUD right-justifies against, and BOTTOM is the HUD's own floor.
    private hudBounds = () => {
        const screenWidth = ScreenDimensions.w + TileSize * 4.7
        return { left: -this.container.x, top: 0, right: screenWidth - this.container.x, bottom: BOTTOM }
    }

    private clampToScreen = (at: Coordinate, width: number, height: number): Coordinate =>
        clampPanelWithin(at, { width, height }, this.hudBounds())

    // The dragged position is a per-viewer convenience, so it lives in localStorage — which can throw
    // or come back empty (private windows, blocked site data), and whose contents are whatever was
    // last written there, hence the guards and the shape check.
    private loadEmberMinimapPlacement = (): Coordinate | null => {
        try {
            if (typeof localStorage !== "undefined") {
                const stored = localStorage.getItem(this.EMBER_MINIMAP_PLACEMENT_KEY)
                const at = stored ? JSON.parse(stored) : null
                if (isFinite(at?.x) && isFinite(at?.y)) {
                    return { x: at.x, y: at.y }
                }
            }
        } catch (error) {
            console.warn("Failed to load minimap placement from localStorage:", error)
        }
        return null
    }

    private saveEmberMinimapPlacement = () => {
        if (!this.emberMinimapPlacement) {
            return
        }
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.EMBER_MINIMAP_PLACEMENT_KEY, JSON.stringify(this.emberMinimapPlacement))
            }
        } catch (error) {
            console.warn("Failed to save minimap placement to localStorage:", error)
        }
    }

    // The ember room's per-room minimap snapshot (camp members + wave attackers). null takes the map
    // down.
    updateEmberMinimap = (minimap: EmberMinimapState | null) => {
        this.emberMinimap.update(minimap)
        this.refreshEmberMinimapTrees(minimap)
        this.positionEmberMinimap()
    }

    // The forest under the dots. Trees are TILES, and the client already holds the whole room's tile
    // map, so they are scanned here instead of riding the wire — the broadcast carries only what moves.
    // Keyed on the map's collision version so a felled tree drops off the map, and floored on
    // EMBER_TREE_RESCAN_MS so a run of tile edits can't turn a 5Hz broadcast into a 5Hz full-room scan.
    private static readonly EMBER_TREE_RESCAN_MS = 1000
    private refreshEmberMinimapTrees = (minimap: EmberMinimapState | null) => {
        if (!minimap) {
            this.emberTreeScanKey = null
            return
        }
        const provider = this.logic.tilemapMetaProvider
        if (!provider) {
            return
        }
        const key = `${this.currentRoomId}:${provider.getCollisionVersion()}:${minimap.width}x${minimap.height}`
        if (key === this.emberTreeScanKey) {
            return
        }
        const now = Date.now()
        // A brand-new room (nothing scanned yet) is drawn immediately; only RE-scans wait their turn.
        if (this.emberTreeScanKey !== null && now - this.emberTreeScanAt < GuiManager.EMBER_TREE_RESCAN_MS) {
            return
        }
        this.emberTreeScanKey = key
        this.emberTreeScanAt = now
        const tilesWide = this.emberMinimap.tilesWide()
        const tilesHigh = this.emberMinimap.tilesHigh()
        this.emberMinimap.updateTrees(collectTreeCells(provider, tilesWide, tilesHigh), tilesWide, tilesHigh)
    }

    // The PiP only earns its screen space while the watched character is OFF-screen. Hide the PiP
    // once the subject enters the player's viewport; show it again when it
    // leaves. Called every frame (updateTimer) so it tracks the subject's movement.
    private updateFollowCamVisibility = () => {
        if (!this.followCam?.isOpen()) {
            this.followCamShown = false
            return
        }
        const focusId = this.followCam.focusEntityId
        const entity = focusId ? this.logic.getEntity(focusId) : null
        // Tracked entity gone (removed) or destroyed -> close the PiP.
        if (!entity || this.logic.isDestroyed(entity.entityId)) {
            this.closeFollowCam()
            return
        }
        const onScreen = this.logic.isWithinPlayerViewport(entity.location)
        const shouldShow = !onScreen

        // Only start a fade on an actual transition (this runs every frame).
        if (shouldShow !== this.followCamShown) {
            this.followCamShown = shouldShow
            this.followCamFader?.stop(true)
            if (shouldShow) {
                this.followCam.visible = true
                // minAlpha (the fade target) passed explicitly: Fader computes its default before
                // applying `direction`, so an "in" fade would otherwise target 0 and never appear.
                this.followCamFader = new Fader(this.followCam, { direction: "in", minAlpha: 1.0 })
            } else {
                this.followCamFader = new Fader(this.followCam, {
                    direction: "out",
                    minAlpha: 0,
                    releaseCallback: () => {
                        this.followCam.visible = false
                    },
                })
            }
            this.followCamFader.update()
        }

    }

    // Open the follow-cam PiP on an entity: show the modal, keep the entity's neighborhood live on
    // the client, and ask the server to sync it in full detail every tick.
    openFollowCam = (entityId: EntityId, name?: string) => {
        // Dismiss roster menus when opening the follow-cam.
        if (this.commandWheelRosterId) {
            this.hideCommandWheel()
        }
        if (this.rolePickerRosterId) {
            this.hideRolePicker()
        }
        this.followCam.open(entityId, name)
        // Start transparent and let updateFollowCamVisibility fade it in (null forces a fade).
        this.followCam.alpha = 0
        this.followCamShown = null
        this.positionFollowCam()
        // Refresh the map alongside the PiP.
        this.positionEmberMinimap()
        this.logic.setFocusEntityId(entityId)
        this.logic.playerController.client.setFocusEntity(entityId)
    }

    // Close the follow-cam if it's open (Escape / deactivation); no-op otherwise.
    dismissFollowCam = () => {
        if (this.followCam?.isOpen()) {
            this.closeFollowCam()
        }
    }

    closeFollowCam = () => {
        this.followCamFader?.stop(true)
        this.followCamShown = false
        this.followCam.close()
        this.emberRoster.clearSelection()
        // Refresh the map after closing the PiP.
        this.positionEmberMinimap()
        this.logic.setFocusEntityId(null)
        this.logic.playerController.client.setFocusEntity(null)
    }

    updateBannerText = (text: string) => {
        this.bannerText.text = text
    }

    updateLocation = (label: string) => {
        //
    }

    startChat = () => {
        // The two fields are the same conversation; only one of them can hold the keyboard.
        this.dismissChatComposer()
        this.chatOpen = true
        this.chatInput = EMPTY_INPUT
        this.chatWalk = { ...this.chatWalk, index: -1, draft: "" }
        this.renderChatInput()

        clearTimeout(this.chatHistoryTextTimer)
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        this.chatHistory.alpha = 1.0
        this.chatHistory.show()
    }

    // --- the composer: chat on a device with no keyboard ------------------------------------

    // Put the composer up (gui/chat_composer.ts). The inline field and the composer are never both
    // open: they are the same conversation, and they would both be holding the keyboard.
    public openChatComposer = () => {
        if (!this.chatComposer || this.chatComposer.isOpen()) {
            return
        }
        this.dismissChatInput()
        this.chatOpen = true
        // The log moves with the field it belongs to: docked under the composer at the top of the
        // screen, where no soft keyboard can cover it, and pinned there -- a log that faded out
        // from under a half-typed reply would take the conversation away mid-sentence.
        clearTimeout(this.chatHistoryTextTimer)
        this.chatHistoryFader?.stop(true)
        this.chatHistory.alpha = 1.0
        this.chatHistory.x = COMPOSER_LOG_X
        this.chatHistory.setAnchor("top", COMPOSER_LOG_TOP)
        this.chatHistory.show()
        // The field owns the keyboard from here (see Keyboard.suppressTyping), which also releases
        // whatever was held when it opened -- the movement loop reads that map every frame, so
        // clearing it is what stops the player walking while they type.
        Keyboard.suppressTyping(true)
        this.chatComposer.open()
    }

    public dismissChatComposer = () => {
        if (!this.chatComposer?.isOpen()) {
            return
        }
        // The press that is closing this panel is still travelling. pixi delivers `pointerdown` to
        // the scrim BEFORE the document-level `mousedown` the world action arrives on, so by the
        // time that one is handled the composer is already shut and isChatComposerVisible() no
        // longer gates it -- measured live: a tap on the dimmed world closed the composer and fired
        // a shot. Holding attacks off for a beat is what an interaction icon does with the same
        // problem (see Player.suppressAttack).
        this.logic.playerController?.suppressAttack()
        this.chatComposer.close()
        Keyboard.suppressTyping(false)
        this.chatOpen = false
        // Back to the ambient log, bottom-left, which fades itself out again.
        this.chatHistory.x = CHAT_LEFT
        this.chatHistory.setAnchor("bottom", CHAT_LOG_BOTTOM)
        this.scheduleChatHistoryFade(5000)
    }

    public isChatComposerVisible = (): boolean => !!this.chatComposer?.isOpen()

    // Browser-console triggers, in the same shape as window.playersFlare / window.rosterWidget:
    //
    //     openChat()     // put the composer up at a desktop, where Enter opens the inline field
    //     chatFlare()    // flare the SAY chip as if a line had landed while it was closed
    //     chatComposer   // the panel itself: isOpen(), text()
    //
    // The composer is the TOUCH path, so nothing a developer at a keyboard can do opens it -- there
    // would otherwise be no way to look at it without a phone in hand.
    private exposeChatDevTriggers = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).chatComposer = this.chatComposer
        ;(window as any).openChat = (): string => {
            this.openChatComposer()
            return "[openChat] composer up"
        }
        ;(window as any).chatFlare = (): string => {
            this.chatChip?.flash()
            return `[chatFlare] chip ${this.chatChip?.visible ? "up" : "hidden (not in a camp)"}`
        }
    }

    // Redraws the input from the current state. Everything that edits the line ends here.
    private renderChatInput = () => {
        this.typing.update(this.chatInput)
    }

    // Takes the input off the screen, and the log with it, dropping the half-written line. Every
    // client-side slash command below starts this way before doing its own thing.
    private dismissChatInput = () => {
        this.chatOpen = false
        this.chatInput = EMPTY_INPUT
        this.typing.removeText()
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        this.chatHistory.alpha = 1.0
        this.chatHistory.hide()
    }

    appendChatHistory = (message: string, speaker?: string) => {
        if (!message) {
            return
        }
        this.chatHistory.show()

        // Callers hand over the speaker and the line apart; a single formatted "Name: line" is
        // still understood, because that is what this took for its whole life before.
        const named = speaker === undefined ? entryFromLine(message) : { speaker, text: message }
        this.chatLog.push({ ...named, self: !!named.speaker && named.speaker === this.logic?.playerEntity?.name })
        this.chatLog = this.chatLog.slice(-LOG_MAX_MESSAGES)
        this.chatHistory.updateEntries(this.chatLog)

        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        clearTimeout(this.chatHistoryTextTimer)
        this.chatHistory.alpha = 1.0

        // A line that lands while the player is typing does NOT start the clock: fading the log
        // out from under someone who is in the middle of answering it is how a reply gets sent to
        // a conversation the sender can no longer see. stopChat starts it again on the way out.
        if (this.chatOpen) {
            return
        }
        // Nobody is reading, so this line is about to fade. On touch the chip's flare is the only
        // notice the player gets that it was ever there: there is no keyboard to open chat with,
        // and the log itself is deliberately not made tappable (it is drawn over the move stick, and
        // an interactive panel there would swallow the thumb that steers). The chip rate-limits
        // itself to one flare at a time, so a busy conversation does not leave the corner blinking.
        if (isMobile()) {
            this.chatChip?.flash()
        }
        this.scheduleChatHistoryFade(5000)
    }

    private scheduleChatHistoryFade = (delay: number) => {
        clearTimeout(this.chatHistoryTextTimer)
        this.chatHistoryTextTimer = setTimeout(() => {
            this.chatHistoryFader = new Fader(this.chatHistory, {
                direction: "out",
                minAlpha: 0.0,
                fadeInterval: 16, // ~60fps
                fadeAlphaDecrement: 0.02,
                releaseCallback: () => {
                    this.chatHistory.hide()
                    this.chatHistory.alpha = 1.0 // Reset alpha for next time
                },
            })
            this.chatHistoryFader.update()
        }, delay)
    }

    // One keystroke of chat input. `mods` is the modifier state at the moment the key went down,
    // which is what tells a plain Backspace from a word-wise one -- and what keeps the letter of
    // an OS shortcut (cmd+R) out of the line.
    captureKeystroke = (c: string, mods?: KeyModifiers) => {
        const scope: EditScope = mods?.meta ? "line" : mods?.alt || mods?.ctrl ? "word" : "char"

        if (c === "Escape") {
            // player.ts closes the input right after this; dropping the line here is what makes
            // Escape a cancel rather than a send.
            this.chatInput = EMPTY_INPUT
            this.chatWalk = { ...this.chatWalk, index: -1, draft: "" }
            return
        }

        if (c === "Backspace") {
            this.chatInput = deleteBackward(this.chatInput, scope)
        } else if (c === "Delete") {
            this.chatInput = deleteForward(this.chatInput, scope)
        } else if (c === "ArrowLeft" || c === "ArrowRight") {
            const back = c === "ArrowLeft"
            // cmd+arrow is "to the end of the line" on a Mac, not "a word at a time".
            this.chatInput = mods?.meta
                ? moveCaret(this.chatInput, back ? "home" : "end")
                : moveCaret(this.chatInput, back ? "left" : "right", scope === "word")
        } else if (c === "Home" || c === "End") {
            this.chatInput = moveCaret(this.chatInput, c === "Home" ? "home" : "end")
        } else if (c === "ArrowUp" || c === "ArrowDown") {
            const walked =
                c === "ArrowUp"
                    ? historyBack(this.chatWalk, this.chatInput)
                    : historyForward(this.chatWalk, this.chatInput)
            this.chatWalk = walked.walk
            this.chatInput = walked.input
        } else if (mods?.ctrl) {
            // The line-editing keys a terminal (and a browser text field) answers to.
            switch (c.toLowerCase()) {
                case "u":
                    this.chatInput = deleteBackward(this.chatInput, "line")
                    break
                case "k":
                    this.chatInput = deleteForward(this.chatInput, "line")
                    break
                case "w":
                    this.chatInput = deleteBackward(this.chatInput, "word")
                    break
                case "a":
                    this.chatInput = moveCaret(this.chatInput, "home")
                    break
                case "e":
                    this.chatInput = moveCaret(this.chatInput, "end")
                    break
                default:
                    return
            }
        } else if (mods?.meta) {
            // cmd+R, cmd+L, cmd+1 ... the player is talking to the browser, not to the camp.
            return
        } else if (c.length === 1) {
            this.chatInput = insertText(this.chatInput, c)
        } else {
            return
        }

        this.renderChatInput()
    }

    // Pasted text (see Keyboard.addPasteListener): dropped in at the caret, flattened to one line
    // and trimmed to whatever is left under the length cap.
    insertChatText = (text: string) => {
        this.chatInput = insertText(this.chatInput, text)
        this.renderChatInput()
    }

    stopChat = (): string => {
        this.chatOpen = false
        // Stop any active fader and timeout
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        clearTimeout(this.chatHistoryTextTimer)
        const messageToSend = this.chatInput.value.trim()

        if (messageToSend === "/register") {
            this.dismissChatInput()

            if (this.logic.playerController.isGuest()) {
                this.collectPlayerAccount(
                    this.logic.playerEntity.name,
                    (username: string, password: string, email: string) => {
                        if (username && password) {
                            this.logic.playerController.client.register(username, password, email)
                        }
                    },
                )
            } else {
                this.addScreenMessage({
                    target: "important",
                    text: "You're already registered",
                })
            }

            return ""
        }

        if (messageToSend === "/message") {
            this.dismissChatInput()

            if (this.incomingMessageMeta.text) {
                this.doDynamicDialog(this.incomingMessageMeta.text)
                this.haltMessageIndicator()
            }
            return ""
        }

        if (messageToSend === "/help") {
            this.logic.playerController.client.logActivity("invoke help")

            this.dismissChatInput()
            this.help(() => {})

            return ""
        }

        if (messageToSend === "/upgrade") {
            this.logic.playerController.client.invokeStatUpgradeUI()
            this.dismissChatInput()

            return ""
        }

        if (messageToSend === "/about") {
            this.logic.playerController.client.logActivity("invoke about")

            this.dismissChatInput()
            this.about()

            return ""
        }

        if (messageToSend.startsWith("/timer")) {
            this.logic.playerController.client.logActivity("invoke timer")

            this.dismissChatInput()

            // Parse timer duration from command (e.g., "/timer 30" for 30 seconds)
            const parts = messageToSend.split(" ")
            const duration = parts.length > 1 ? parseInt(parts[1]) : 10 // default 10 seconds

            if (isNaN(duration) || duration <= 0) {
                this.addScreenMessage({
                    target: "important",
                    text: "Invalid timer duration. Use: /timer [seconds]",
                })
            } else {
                this.countdownTimer.visible = true
                this.countdownTimer.start(duration)
            }

            return ""
        }

        if (messageToSend === "/timerstop") {
            this.logic.playerController.client.logActivity("stop timer")

            this.dismissChatInput()

            if (this.countdownTimer.visible) {
                this.countdownTimer.stop()
                this.countdownTimer.visible = false
                this.addScreenMessage({
                    target: "important",
                    text: "Timer stopped",
                })
            } else {
                this.addScreenMessage({
                    target: "important",
                    text: "No timer is currently running",
                })
            }

            return ""
        }

        if (messageToSend === "/timerreset") {
            this.logic.playerController.client.logActivity("reset timer")

            this.dismissChatInput()

            this.countdownTimer.reset()
            this.countdownTimer.visible = false
            this.addScreenMessage({
                target: "important",
                text: "Timer reset",
            })

            return ""
        }

        if (messageToSend === "/wheel") {
            this.logic.playerController.client.logActivity("invoke command wheel")

            this.dismissChatInput()

            this.commandWheel.fadeIn()
            this.addScreenMessage({
                text: "Command wheel opened. Click commands to select, use button to switch NPCs.",
            })

            return ""
        }

        if (messageToSend === "/wheelclose") {
            this.logic.playerController.client.logActivity("close command wheel")

            this.dismissChatInput()

            this.commandWheel.fadeOut()
            this.addScreenMessage({
                text: "Command wheel closed",
            })

            return ""
        }

        if (messageToSend.startsWith("/hero")) {
            this.logic.playerController.client.logActivity("test hero message")

            this.dismissChatInput()

            // Parse text and duration from command
            // Format: /hero [duration] [text] or /hero [text]
            const parts = messageToSend.split(" ")
            let text = "HERO MESSAGE!" // default text
            let duration = 2000 // default 2 seconds

            if (parts.length > 1) {
                // Check if second part is a number (duration)
                const secondPart = parseInt(parts[1])
                if (!isNaN(secondPart)) {
                    // Format: /hero [duration] [text]
                    duration = secondPart
                    text = parts.slice(2).join(" ") || "HERO MESSAGE!"
                } else {
                    // Format: /hero [text]
                    text = parts.slice(1).join(" ")
                }
            }

            this.addScreenMessage({
                target: "hero",
                text: text,
                duration: duration,
            })

            return ""
        }

        // The log outlives the input it was opened with, so the player can read the answer to
        // what they just sent.
        if (this.chatHistoryFader) {
            this.chatHistoryFader.stop(true)
        }
        this.scheduleChatHistoryFade(10000)

        // Remember what was sent for the up-arrow walk (consecutive duplicates collapse), and
        // reset the walk so the next Up starts from this line rather than mid-history.
        this.chatWalk = rememberSent(this.chatWalk, messageToSend)
        if (messageToSend) {
            this.saveChatHistory()
        }

        this.chatInput = EMPTY_INPUT
        this.typing.removeText()

        return messageToSend
    }

    private saveChatHistory = () => {
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(this.CHAT_HISTORY_KEY, JSON.stringify(this.chatWalk.entries))
            }
        } catch (error) {
            console.warn("Failed to save chat history to localStorage:", error)
        }
    }

    private loadChatHistory = () => {
        try {
            if (typeof localStorage !== "undefined") {
                const saved = localStorage.getItem(this.CHAT_HISTORY_KEY)
                if (saved) {
                    const parsed = JSON.parse(saved)
                    if (Array.isArray(parsed)) {
                        this.chatWalk = { entries: parsed.filter(entry => typeof entry === "string"), index: -1, draft: "" }
                    }
                }
            }
        } catch (error) {
            console.warn("Failed to load chat history from localStorage:", error)
            this.chatWalk = EMPTY_WALK
        }
    }

    clearChatHistory = () => {
        this.chatWalk = EMPTY_WALK
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.removeItem(this.CHAT_HISTORY_KEY)
            }
        } catch (error) {
            console.warn("Failed to clear chat history from localStorage:", error)
        }
    }

    addScreenMessage = (message: ScreenMessage) => {
        const { target, text, roomId, action, actionToken, context, suppressOnCloseEmitEvent } = message
        if (suppressOnCloseEmitEvent && this.currentModal) {
            this.currentModal.modal.suppressOnCloseCallback = suppressOnCloseEmitEvent
        }

        if (!!roomId && roomId !== this.currentRoomId) {
            return
        }

        if (action === "close" && target !== "featureWidget") {
            this.closeModal()
            return
        }

        if (action === "openDynamicDialog") {
            this.dynamicDialogToken = actionToken
        }

        if (!target) {
            this.screenMessages.push(message)
            // Always consume messages immediately to allow interruption
            this.consumeMessages()
        } else {
            if (target === "leaderboard") {
                this.updateBannerText(text)
            }
            if (target === "important") {
                this.importantMessage.update(message)
            }
            if (target === "waveStatus") {
                this.updateWaveIndicator(message)
            }
            if (target === "countdown") {
                const duration = parseInt(text)
                if (!isNaN(duration) && duration > 0) {
                    // The dial and the wave badge share one slot: starting a countdown means the
                    // previous wave is over, so the badge comes down.
                    this.waveIndicator.update(null)
                    this.countdownTimer.visible = true
                    this.countdownTimer.start(duration)
                } else if (!isNaN(duration)) {
                    // A non-positive value is an explicit "hide the countdown" signal (e.g. the ember
                    // waves were stopped on a delegated respawn). Clear the ring immediately instead of
                    // letting its client-side timer run down.
                    this.countdownTimer.stop()
                    this.countdownTimer.visible = false
                } else {
                    this.addScreenMessage({
                        target: "important",
                        text: "Invalid countdown duration. Must be a positive number.",
                    })
                }
            }
            if (target === "hero") {
                this.heroMessage.show(message)
            }
            if (target === "dialog") {
                if (message.forceTarget) {
                    this.doDynamicDialog(message)
                } else {
                    this.availableMessage(message)
                }
            }
            if (target === "stickyBanner") {
                this.stickyBanner.setup(message.text, message.context?.icon, message.context?.hoverText)
                this.stickyBanner.x = this.controlBar.x - this.stickyBanner.width - TileSize
                this.dayCycle.x = this.stickyBanner.x - TileSize * 1.25
            }
            if (target === "featureWidget") {
                this.featureWidget.setup(message)
            }
        }
    }

    updateLeaderboard = (leaderboards: LeaderboardMeta[] | null) => {
        this.leaderboard.update(leaderboards)
    }

    updateInventory = (owner: Entity, inventory: Inventory) => {
        if (this.inventoryUpdateTs.get(owner.entityId) === inventory.lastUpdateTs) {
            return false
        }

        this.inventoryUpdateTs.set(owner.entityId, inventory.lastUpdateTs)

        const player = this.getPlayer()
        if (player.entityId === owner.entityId) {
            if (this.dynamicDialogToken) {
                this.logic.emitConversationResponse(this.dynamicDialogToken)
            }
            // Worked out BEFORE the tray is drawn, because the tray now carries the count: the
            // arrows used to be read off the ResourceBar beside the player's NAME, in the opposite
            // corner from the bow they belong to.
            const ammoBearingWeapon = locateInventoryItemObjects(inventory, "weapon")
                .sort((b, a) => (a.active ? 2 : 1 - (b.active ? 2 : 1)))
                .find((o: InventoryItem) => {
                    return !!WeaponAmmo[o.itemType]
                })

            let ammo: AmmoReadout | undefined
            if (ammoBearingWeapon) {
                const ammoType: ItemTypeMeta = resolveItemTypeToMeta(WeaponAmmo[ammoBearingWeapon.itemTypeMeta.type])
                if (ammoType) {
                    // Only while the bow is actually IN HAND. A bow sitting in the pack makes the
                    // arrow count noise -- it is not what the next click is going to spend.
                    if (ammoBearingWeapon.active) {
                        const ammoItem = locateInventoryItemObjectByItemType(inventory, ammoType.type)
                        ammo = {
                            icon: `${ammoType.type}-${ammoType.color}.png`,
                            quantity: ammoItem?.quantity || 0,
                        }
                    }
                    this.logic.playerController.updateLongRangedEquippedState(ammoBearingWeapon.active)
                } else {
                    this.logic.playerController.updateLongRangedEquippedState(false)
                }
            } else {
                this.logic.playerController.updateLongRangedEquippedState(false)
            }

            this.slotCallbacks = this.equipped.update(
                inventory,
                (itemId: string) => {
                    this.logic.playerController.selectSlot(itemId)
                },
                ammo,
            )

            this.abilities.update(inventory, (shortcut: string) => {
                this.logic.playerController.activeAbility(shortcut)
            })

            this.abilities.x = this.equipped.x + this.equipped.width

            const shards = locateInventoryItemObjectByItemType(inventory, "shard-metal")
            this.resourceBar.setup([
                {
                    icon: `shard-metal-gray.png`,
                    quantity: shards?.quantity || 0,
                },
            ])
            this.resourceBar.x = this.nameText.x + this.nameText.width + 20
            // Honor per-room suppression (e.g. ember rooms) — the ammo branch above may have
            // forced it visible; keep it hidden while suppressed.
            this.resourceBar.visible = !this.suppressShards
        }

        // this.inventoryGui.update()

        return true
    }

    activateInventorySlot = (slot: number) => {
        const callback = this.slotCallbacks[slot]
        if (callback) {
            callback()
        }
    }

    mouseOverControl = (disable: boolean) => {
        this.logic.enablePlayerMouseAction(!disable)
    }

    private consumeMessages = () => {
        // Clear any existing timeout to allow interruption
        if (this.currentMessageTimeout) {
            clearTimeout(this.currentMessageTimeout)
            this.currentMessageTimeout = null
        }

        // If there's a current message, interrupt it and process the next one
        if (this.currentScreenMessage) {
            if (this.currentScreenMessage.displayCallback) {
                this.currentScreenMessage.displayCallback()
            }
            this.currentScreenMessage = null
        }

        this.currentScreenMessage = this.screenMessages.shift()
        if (!this.currentScreenMessage) {
            return
        }

        const { text, roomId, displayCallback } = this.currentScreenMessage
        let delayMs = 0
        if (!roomId || roomId === this.currentRoomId) {
            this.bannerText.text = text

            const wordCount = text.split(" ").length
            delayMs = Math.max(1000, ((wordCount * 60) / 200) * 1000)
        }

        this.currentMessageTimeout = setTimeout(() => {
            if (displayCallback) {
                displayCallback()
            }
            this.bannerText.text = ""
            this.currentScreenMessage = null
            this.currentMessageTimeout = null
            this.consumeMessages()
        }, delayMs)
    }

    fadeOut = (immediate?: boolean) => {
        this.blackout.alpha = 0.0
        this.blackout.visible = true
        this.bannerText.text = ""

        // Clear any pending message timeout
        if (this.currentMessageTimeout) {
            clearTimeout(this.currentMessageTimeout)
            this.currentMessageTimeout = null
        }
        this.currentScreenMessage = null
        if (immediate) {
            this.blackout.alpha = 1.0
        } else {
            const loop = setInterval(() => {
                const newAlpha = Math.min(1.0, this.blackout.alpha + 0.1)
                this.blackout.alpha = newAlpha

                if (newAlpha === 1.0) {
                    clearInterval(loop)
                }
            }, 100)
        }
    }

    /**
     * Close the curtain and leave it closed, with none of fadeOut's housekeeping (it also clears the
     * banner and the message queue, which a room load has no business doing).
     *
     * Used to hold a freshly loaded room out of sight while it finishes arriving: fadeIn starts from
     * full black anyway, so this only brings that black forward to the moment the room lands rather
     * than the moment the screen opens on it. See RoomReveal.
     */
    holdBlackout = () => {
        this.blackout.alpha = 1.0
        this.blackout.visible = true
    }

    fadeIn = () => {
        this.blackout.alpha = 1.0
        this.blackout.visible = true
        const loop = setInterval(() => {
            const newAlpha = Math.max(0.0, this.blackout.alpha - 0.1)
            this.blackout.alpha = newAlpha

            if (newAlpha === 0.0) {
                clearInterval(loop)
                this.blackout.alpha = 0.0
                this.blackout.visible = false
            }
        }, 100)
    }

    roomUpdated = (
        roomId: EntityId,
        roomName?: string,
        suppressMinimap?: boolean,
        suppressDayCycle?: boolean,
        suppressControlBar?: boolean,
        suppressLevelUp?: boolean,
        suppressMana?: boolean,
        suppressFood?: boolean,
        suppressShards?: boolean,
        suppressPlayerName?: boolean,
        suppressTutorial?: boolean,
        suppressGuestPrompt?: boolean,
    ) => {
        // On an ACTUAL room change, clear any stale wave countdown from the previous room. The
        // countdown ring is per-room wave UI driven purely by a client-side timer, so nothing else
        // hides it when the room goes away (e.g. after dying and respawning into a fresh ember camp).
        // Guarded on a real change so it never cancels a countdown that's legitimately running in the
        // room we're already in (roomUpdated can fire repeatedly for the same room).
        if (roomId !== this.currentRoomId && this.countdownTimer) {
            this.countdownTimer.reset()
            this.countdownTimer.visible = false
            // Same for the wave badge: it's per-room wave UI, and the room it belonged to is gone.
            this.waveIndicator?.update(null)
        }

        // The ember clues belong to the camp that armed them. A real room change ends that camp, so
        // they come down with it -- guarded on the change, because roomUpdated fires repeatedly for
        // the room we are already in and an armed clue must survive those.
        if (roomId !== this.currentRoomId) {
            this.emberInviteModal?.close()
            this.recruitHintRoster = []
            this.recruitMenuHint.reset()
            this.campfireMenuHint.reset()
            this.tutorial.clearEmberClues()
            // Nothing typed at one camp should still be on screen over the next one -- and a DOM
            // field left up keeps a phone's keyboard over the room that is loading behind it.
            this.dismissChatComposer()
        }

        this.currentRoomId = roomId
        this.currenRoomName = roomName
        this.screenMessages = this.screenMessages.filter(n => !n.roomId || n.roomId === roomId)
        this.controlBar.visible = !suppressControlBar
        // The pack has to stay reachable. Suppressing the control bar takes the inventory button
        // with it, and in a camp there is no keyboard to fall back on, so the chip in the top-right
        // row stands in for exactly as long as the bar is gone.
        this.emberInventoryChip.visible = !!suppressControlBar
        // Chat's button follows the pack's: whenever the bar that carries it is gone, the chip in
        // the top-right row stands in for it.
        this.chatChip.visible = !!suppressControlBar
        this.layoutChipRow()
        // Latch per-room mana / food (hunger) meter suppression so it survives later update() calls,
        // which otherwise force the meters visible on any mana/hunger change. Hide immediately on
        // entry (and the meters are restored on exit when the flags come back false).
        this.suppressMana = !!suppressMana
        this.suppressFood = !!suppressFood
        if (this.suppressMana) {
            this.mana.visible = false
        }
        if (this.suppressFood) {
            this.hunger.visible = false
        }
        // Latch per-room shard/resource counter + player-name suppression. updateInventory() only
        // fires when the inventory changes, so apply the resourceBar state here on entry too; the
        // name label is applied directly here (update() never toggles its visibility).
        this.suppressShards = !!suppressShards
        this.suppressPlayerName = !!suppressPlayerName
        this.resourceBar.visible = !this.suppressShards
        this.nameText.visible = !this.suppressPlayerName
        // Latch per-room day/night suppression so it survives later updateDayPct() calls, which
        // otherwise force the widget visible on every day-pct tick. Hide immediately on entry too.
        this.suppressDayCycle = !!suppressDayCycle
        if (this.suppressDayCycle) {
            this.dayCycle.visible = false
        }
        // Latch per-room "Level up stats!" suppression and rebuild the help-messaging stack so a
        // button from the previous room is removed on entry (and restored on exit).
        this.suppressLevelUp = !!suppressLevelUp
        // Latch per-room tutorial suppression so showTutorial() is a no-op while in the room (and
        // popups return once the flag comes back false on exit). Ember rooms set this.
        this.tutorial.suppressed = !!suppressTutorial
        // Same for a guest's "/register to save progress" line, which helpMessaging() rebuilds from
        // scratch just below -- so setting it here is what takes the line off on entry and puts it
        // back on exit.
        this.suppressGuestPrompt = !!suppressGuestPrompt
        this.helpMessaging()
        this.minimapSurface.roomUpdated(this.userSuppressMinimap || suppressMinimap)
        this.featureWidget.remove()
        // Roster is per-room and ember-only; clear it on any room change so it never lingers
        // after leaving (the server simply stops sending it in non-ember rooms). Same for the
        // minimap, which is a plan of the room we're leaving.
        // clear(), not update([]): the cards are not dying, the player is leaving the room, and a
        // card cannot break apart over a room that is already gone.
        this.emberRoster.clearRoster()
        this.positionEmberRoster()
        this.updateEmberMinimap(null)
        // The player list is ember-only too, and it is about the server rather than the room -- but a
        // room change is the one moment we know the feed may stop, so it goes down with the rest and
        // comes back on the next broadcast.
        this.updateEmberPlayers([])
        // Close any open follow-cam on room change (clears the server focus subscription too).
        if (this.followCam?.isOpen()) {
            this.closeFollowCam()
        }
    }

    hideRoomName = (hidden: boolean) => {
        this.roomNameHidden = hidden
    }

    animateAbility = (abilityType: AbilityType, duration?: number) => {
        const pulsar = this.abilities.slotPulsar.get(abilityType)
        if (!pulsar) {
            return
        }

        pulsar.pulse(duration || 750)
    }

    animateLocalEntityCollection = (entity: Entity, target: string, position?: number) => {
        // for now -- all local entity animations relate to ability slot
        const abilityType = target.split(":")[1]
        const location = this.abilities.slotCoordinates.get(abilityType)
        if (!location) {
            return
        }

        const destX = this.abilities.x + location.x + 3
        const destY = this.abilities.y + location.y - 10

        const entityRenderingManager: EntityRenderingManager =
            this.logic.clientRenderer.entity() as EntityRenderingManager
        const clientRenderable: ClientRenderable = entityRenderingManager.renderEntity(entity)

        this.stage.addChild(clientRenderable)
        clientRenderable.x = ScreenDimensions.w * 0.58
        clientRenderable.y = 285

        const increment = 6
        const animationInterval = setInterval(() => {
            const currentDistance = getDistance(clientRenderable.x, clientRenderable.y, destX, destY)
            if (currentDistance < increment) {
                clearInterval(animationInterval)
                // clientRenderable.remove()
                const fadeInterval = setInterval(() => {
                    if (clientRenderable.alpha <= 0.0) {
                        clearInterval(fadeInterval)
                        clientRenderable.remove()
                        return
                    }

                    clientRenderable.alpha -= 0.01
                }, 1)
                return
            }
            if (clientRenderable.alpha > 0.6) {
                clientRenderable.alpha -= 0.01
            }

            clientRenderable.x += clientRenderable.x > destX ? -increment : increment
            clientRenderable.y += clientRenderable.y > destY ? -increment : increment
        }, 1)
    }

    /**
     * The two touch sticks.
     *
     * Neither is pinned any more: a stick is summoned under the thumb that asks for it and fades
     * when that thumb lifts, so a phone screen costs nothing to a control nobody is holding. The
     * left half of the screen summons this move stick, the right half the attack stick; FloatingSticks
     * owns the gesture, the halves and the "is this touch free?" question.
     */
    setupJoystick = (container: Container) => {
        if (!isMobile()) {
            return
        }

        const moveStick = new FloatingJoystick({
            outer: Sprite.from("outer"),
            inner: Sprite.from("inner"),

            outerScale: { x: 0.5, y: 0.5 },
            innerScale: { x: 0.8, y: 0.8 },

            // Held intent, not a direct write. Player's 60Hz velocity loop rebuilds
            // speed2 from held input every tick, so anything written onto entity.speed2
            // here is erased before the next frame -- and since onChange only fires as the
            // thumb travels, a thumb held at full deflection never writes again. That pair
            // is what made the stick stutter and refuse to move continuously.
            onChange: data => {
                const entity = this.logic.playerEntity
                const { power } = data
                if (power < JoystickDeadZone) {
                    this.logic.playerController.setJoystickIntent(null)
                    return
                }

                const vector = JoystickVectors[data.direction]
                if (!vector) {
                    return
                }

                // Stick pushed right out to the rim means sprint. See sprintScaledIntent
                // for why scaling the vector is the whole mechanic, and why it must not
                // scale the shared JoystickVectors entry in place.
                const intent = sprintScaledIntent(vector, power)
                this.logic.playerController.setJoystickIntent(intent)

                // Facing follows the move stick unless the aim stick has taken it over.
                // Sprinting scales the vector but not its direction, so the same intent
                // serves for both.
                if (this.leftJoystickAngleAllowed) {
                    entity.movement.angle = getAngle(
                        { x: entity.location.x + intent.x, y: entity.location.y + intent.y },
                        { x: entity.location.x, y: entity.location.y },
                    )
                }
            },

            onStart: () => {
                this.logic.playerController.enabledMouseAction(false)
            },

            onEnd: () => {
                this.logic.playerController.setJoystickIntent(null)
                this.logic.playerController.enabledMouseAction(true)
            },
        })
        container.addChild(moveStick)

        const knob = Sprite.from("inner")
        knob.tint = 0xff0000
        let angleBeforeAim: number
        const aimStick = new FloatingJoystick({
            outer: Sprite.from("outer"),
            inner: knob,

            outerScale: { x: 0.5, y: 0.5 },
            innerScale: { x: 0.8, y: 0.8 },

            onChange: data => {
                const entity = this.logic.playerEntity
                const { power } = data
                if (power < JoystickFirePower) {
                    return
                }
                this.logic.playerController.client.fireBullet()

                const aim = JoystickVectors[data.direction]
                if (!aim) {
                    return
                }

                entity.movement.angle = getAngle(
                    { x: entity.location.x + aim.x, y: entity.location.y + aim.y },
                    { x: entity.location.x, y: entity.location.y },
                )
            },

            onStart: () => {
                this.logic.playerController.enabledMouseAction(false)
                this.leftJoystickAngleAllowed = false
                angleBeforeAim = this.getPlayer().movement.angle
            },

            onEnd: () => {
                this.logic.playerController.enabledMouseAction(true)
                this.leftJoystickAngleAllowed = true
                this.getPlayer().movement.angle = angleBeforeAim
            },
        })
        container.addChild(aimStick)

        this.touchSticks = new FloatingSticks({
            app: getRendererApp(),
            layer: container,
            move: moveStick,
            aim: aimStick,
            // Read per touch rather than cached: the HUD's width comes off the stage, which the
            // letterbox can still be resizing when the first player entity arrives and this runs.
            midline: () => {
                const { left, right } = this.hudBounds()
                return (left + right) / 2
            },
            // A press and release with no steering in between: the attack stick's own way of
            // saying "swing", which the pinned stick got from pixi's `tap`.
            onAimTap: () => this.logic.playerController.client.fireBullet(),
        })
        this.touchSticks.bind()
    }

    socialMedia = (parent?: Container): Graphics => {
        const container = new Graphics()
        container.y = 480
        if (parent) {
            parent.addChild(container)
        }

        const socialMedia = new Graphics()

        const reddit = new Sprite(Loader.shared.resources["reddit"].texture)
        reddit.scale.set(0.05)
        reddit.interactive = true
        reddit.on("click", () => {
            window.open("https://www.reddit.com/r/projectsamsara/")
            this.logic.playerController.client.logActivity(
                `>> ${this.logic.playerEntity?.name || "guest"} visited discord <<`,
            )
        })
        reddit.on("tap", () => {
            window.open("https://www.reddit.com/r/projectsamsara/")
            this.logic.playerController.client.logActivity(
                `>> ${this.logic.playerEntity?.name || "guest"} visited subreddit <<`,
            )
        })

        const discord = new Sprite(Loader.shared.resources["discord"].texture)
        discord.scale.set(0.13)
        discord.x = reddit.x + reddit.width + 12
        discord.y = -1
        discord.interactive = true
        discord.on("click", () => window.open("https://discord.gg/Mbt6UfqW6e"))
        discord.on("tap", () => window.open("https://discord.gg/Mbt6UfqW6e"))

        socialMedia.addChild(reddit)
        socialMedia.addChild(discord)

        container.addChild(socialMedia)

        return container
    }

    collectPlayerAccount = (name: string, doneCallback: Function) => {
        const release = () => {
            this.logic.playerController.enabledKeyboardAction(true)
            this.logic.playerController.enabledMouseAction(true)
        }

        const askForName = () => {
            const dialog = new Graphics()
            this.stage.addChild(dialog)

            const padding = 20

            const container = new Graphics()
            container.x = padding
            container.y = padding
            dialog.addChild(container)

            const bannerText = new TextRenderer().variant("BannerFont").render() as Text
            bannerText.y = 10
            bannerText.text = "Register new player:"
            container.addChild(bannerText)

            const placeHolder = name
            const nameField = new TextInput({
                input: {
                    fontSize: "20pt",
                    padding: "10px",
                    width: "530px",
                    color: "#26272E",
                },
                box: {
                    default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
                    focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
                    disabled: { fill: 0xdbdbdb, rounded: 16 },
                },
            })

            nameField.text = placeHolder
            nameField.y = 50
            nameField.placeholder = placeHolder
            nameField.placeholderColor = 0x000000

            container.addChild(nameField)
            nameField.focus()

            let currentName = placeHolder
            let currentPassword = ""
            let currentEmail = ""

            const submit = () => {
                if (!currentName) {
                    this.addScreenMessage({
                        target: "important",
                        text: "Player name is required",
                    })
                    return false
                }
                if (!currentPassword) {
                    this.addScreenMessage({
                        target: "important",
                        text: "Password is required",
                    })
                    return false
                }

                release()
                doneCallback(currentName, currentPassword, currentEmail)

                return true
            }

            nameField.on("input", text => {
                currentName = text
            })
            nameField.on("keyup", keycode => {
                if (keycode === 27) {
                    release()
                    container.removeChildren()
                    dialog.parent.removeChild(dialog)
                    return
                }

                if (keycode === 13) {
                    passwordField.focus()
                }
            })

            const passwordField = new TextInput({
                input: {
                    fontSize: "20pt",
                    padding: "10px",
                    width: "530px",
                    color: "#26272E",
                },
                box: {
                    default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
                    focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
                    disabled: { fill: 0xdbdbdb, rounded: 16 },
                },
            })

            passwordField._dom_input.type = "password"
            passwordField.y = 115
            passwordField.placeholder = "Password"
            passwordField.placeholderColor = 0x000000
            passwordField.on("input", text => {
                currentPassword = text
            })

            passwordField.on("keyup", keycode => {
                if (keycode === 27) {
                    release()
                    container.removeChildren()
                    dialog.parent.removeChild(dialog)
                    return
                }
                if (keycode === 13) {
                    if (submit()) {
                        container.removeChildren()
                        dialog.parent.removeChild(dialog)
                    }
                }
            })

            container.addChild(passwordField)

            const emailField = new TextInput({
                input: {
                    fontSize: "20pt",
                    padding: "10px",
                    width: "530px",
                    color: "#26272E",
                },
                box: {
                    default: { fill: 0xe8e9f3, rounded: 16, stroke: { color: 0xcbcee0, width: 4 } },
                    focused: { fill: 0xe1e3ee, rounded: 16, stroke: { color: 0xabafc6, width: 4 } },
                    disabled: { fill: 0xdbdbdb, rounded: 16 },
                },
            })

            emailField.y = 180
            emailField.placeholder = "Email (optional)"
            emailField.placeholderColor = 0x000000
            emailField.on("input", text => {
                currentEmail = text
            })
            container.addChild(emailField)

            const buttonContainer = new Graphics()
            buttonContainer.y = 245
            container.addChild(buttonContainer)

            {
                const button = new Graphics()
                button.addChild(new TextRenderer().variant("ImportantFont").render("Cancel") as Text)
                button.x = 125
                button.interactive = true
                button.on("click", () => {
                    release()
                    doneCallback()
                    container.removeChildren()
                    dialog.parent.removeChild(dialog)
                })
                buttonContainer.addChild(button)
            }

            {
                const button = new Graphics()
                const text = new TextRenderer().variant("ImportantFont").render("Register") as Text
                text.tint = 0x00ff00
                button.addChild(text)
                button.interactive = true
                button.on("click", () => {
                    if (submit()) {
                        container.removeChildren()
                        dialog.parent.removeChild(dialog)
                        doneCallback()
                    }
                })
                buttonContainer.addChild(button)
            }

            dialog.x = 15
            dialog.y = 50
            dialog.lineStyle(1, 0xffffff, 0.5)
            dialog.beginFill(0x000000, 0.9)
            dialog.drawRoundedRect(0, 0, dialog.width + padding * 2, dialog.height + padding * 2.5, 8)
            dialog.endFill()
        }

        this.logic.playerController.enabledKeyboardAction(false)
        this.logic.playerController.enabledMouseAction(false)

        askForName()
    }

    help = (doneCallback: Function) => {
        const help = new Help(this)
        help.toggle()
    }

    screenDimensions = (): Dimensions => {
        return {
            w: this.stage.width,
            h: this.stage.height,
        }
    }

    createModal = (props: ModalProps) => {
        const modal = new Modal(this.logic, this.stage, props)
        modal.update()

        return modal
    }

    about = () => {
        const about = new About(this)
        about.toggle()
    }

    doDynamicDialog = (message: ScreenMessage) => {
        if (this.currentModal) {
            this.currentModal.updateContent(message)
            return
        }

        if (!message) {
            return
        }

        // Always render via ModernInventoryDialog. It extends DynamicDialog and self-selects its
        // layout from the payload: the player's own inventory, crafting, and trade/chest screens get
        // the modern client-native panels, while any other server-driven dialog (quests, traders,
        // NPC management) falls through to the generic DynamicDialog renderer and looks exactly as
        // before. It's a drop-in for currentModal and every downstream call (updateContent/close/
        // pulseGridItem).
        const onClose = () => {
            if (message.onCloseEmitEvent) {
                this.logic.playerController.client.emitPlayerEvent(message.onCloseEmitEvent)
            }
            this.logic.playerController.client.onDialogClose()
            this.currentModal = undefined
            this.dynamicDialogToken = undefined
        }
        const dialog = new ModernInventoryDialog(this, this.logic, message, onClose)
        dialog.toggle((clicked: boolean) => {
            if (clicked) {
                // this.dynamicDialogToken = null
                this.closeModal()
            }
        })
        this.currentModal = dialog
    }

    availableMessage = (message: ScreenMessage) => {
        this.incomingMessageMeta.unread = true
        this.incomingMessageMeta.expires = Date.now() + 60 * 1000
        this.incomingMessageMeta.text = { ...message }
        this.helpMessaging()
    }

    haltMessageIndicator = () => {
        clearInterval(this.incomingMessageMeta.flasherInterval)
        this.incomingMessageMeta.unread = false
        this.incomingMessageMeta.expires = null
        this.incomingMessageMeta.text = null
        this.helpMessaging()
    }

    showUpgradeIndicator = () => {}

    closeModal = () => {
        if (this.currentModal) {
            this.currentModal.close()
        }
        this.dynamicDialogToken = null
        this.currentModal = null
    }

    toggleInventory = () => {
        if (!this.dynamicDialogToken) {
            this.logic.invokeInventory(null)
        } else {
            this.closeModal()
        }
    }

    // `c` toggles the crafting screen the way `i` toggles the inventory. Asking the server to open
    // crafting while a screen is already up would only re-dress the modal that's there, so a second
    // press has to close it locally instead.
    toggleCrafting = () => {
        if (!this.dynamicDialogToken) {
            this.logic.invokeCrafting()
        } else {
            this.closeModal()
        }
    }

    toggleCharacterCustomizer = () => {
        this.characterCustomizer.toggle()
    }

    buildCharacter = (entityId: EntityId) => {
        const player = this.getPlayer()
        if (player?.entityId !== entityId) {
            // for now -- you can only build your own character
            return
        }

        if (!this.characterBuilder.isOpen()) {
            this.characterBuilder.toggle(() => {
                this.logic.playerController.client.finalizeCharacter()

                // Close the curtain on the CLICK, not on the teleport that answers it.
                //
                // A character drawn in LIMBO is whisked out of it into a fresh camp, and that
                // teleport brings the usual room-transition blackout with it
                // (ServerGameLogic.teleportEntity -> sendFadeInOut("out")). But it cannot arrive for
                // half a second: the finalize has to cross the socket, the server has to dispatch
                // BUILD_CHARACTER{finalized}, and EmberRoomService has to build a whole camp room.
                // This modal is opaque and comes down in the frame of this very callback, so without
                // this that half second is spent looking at limbo -- a tile-less room the visibility
                // parser gives darkness 1.0, i.e. the new character alone in the void.
                //
                // Only from limbo, because only from limbo is a room change coming. Finalizing in
                // the overworld (EMBER_ENABLED off, where the INTRO campaign opens this screen) goes
                // nowhere, and a curtain nothing lifts would be a worse bug than the one above.
                //
                // "out" is an immediate black (fadeOut(true)), so the modal's disappearance reveals
                // the curtain rather than the void, and the camp's own map build fades back in
                // (ClientGameLogic.updateFromWorldState). The "inOut" form is the watchdog: if no
                // camp ever arrives -- a failed build falls through to rescueFromLimbo, which can
                // itself come up empty -- the screen lifts anyway instead of stranding the player
                // behind a black rectangle.
                if (this.currenRoomName === LIMBO_ROOM_NAME) {
                    this.logic.fadeInOut("inOut", 10000)
                }
            })
        }
    }

    getPlayer = (): Entity => this.logic.playerEntity

    pulseInventoryItem = (itemId: EntityId) => {
        if (!this.currentModal) {
            return
        }
        this.currentModal.pulseGridItem(itemId)
    }

    mapPointHoverListener = (onMouseOver: boolean, label: string) => {
        // this.container.beginFill(0xFF0000)
        // this.container.drawCircle(705, 454, 20)
        // this.container.endFill()

        const position = this.logic.clientRenderer.mouse().state().position
        if (onMouseOver) {
            const container = new DyanmicTextContainer({
                backdrop: true,
            })
            container.append2(
                new DynamicText(label, {
                    font: "ChatFont",
                }),
            )
            this.mouseHoverElement = container
            this.container.addChild(this.mouseHoverElement)
            this.mouseHoverElement.x =
                5 + this.minimapSurface.x + this.minimapSurface.width / 2 - this.mouseHoverElement.width / 2
            this.mouseHoverElement.y = this.minimapSurface.y - this.mouseHoverElement.height + 2
        } else {
            if (this.mouseHoverElement) {
                this.container.removeChild(this.mouseHoverElement)
            }
        }
    }

    createCursorSprite = (): BasicSprite => {
        if (this.mousePointer) {
            return this.mousePointer
        }
        const mousePointer = new BasicSprite({
            scale: 0.3,
            name: "crosshairs.png",
        })

        mousePointer.x = 0
        mousePointer.y = 0
        mousePointer.visible = false
        this.mousePointer = mousePointer

        this.stage.addChild(mousePointer)

        this.logic.clientRenderer?.mouse().addListener(state => {
            mousePointer.x = state.position.x
            mousePointer.y = state.position.y * 0.77
        })

        return mousePointer
    }

    // ---- location picker -------------------------------------------------------------------------
    // The reticle under the pointer while the player chooses a spot on the ground (the defender's
    // "Hold Position" order). GuiManager owns only the drawing and the screen->world conversion;
    // arming the pick, ending it, and what the chosen spot MEANS belong to PlayerController, which
    // owns world presses.

    public showLocationPicker = (caption: string, blockedCaption: string) => {
        this.locationPicker.bindMouse(this.logic.clientRenderer?.mouse())
        this.locationPicker.show(caption, blockedCaption)
    }

    public hideLocationPicker = () => {
        this.locationPicker.hide()
    }

    public isLocationPickerVisible = (): boolean => this.locationPicker.isVisible()

    public setLocationPickerBlocked = (blocked: boolean) => {
        this.locationPicker.setBlocked(blocked)
    }

    // The fine world coordinate under the pointer, read straight off pixi's own transforms: the
    // world-overlay surface carries the same camera transform as the world itself, so `toLocal`
    // plus its scale is an exact inverse of the camera with nothing to keep in sync.
    //
    // Deliberately NOT PlayerController's `pointer`. That one is hand-derived from the camera
    // modifiers and lifted ~10px so a bow aims at a body rather than at the ground under it —
    // exactly the wrong bias for "stand here".
    public worldPointAtPointer = (): Coordinate | null => {
        const overlay = this.logic.clientRenderer?.map()?.overlay()
        const mouse = this.logic.clientRenderer?.mouse()
        if (!overlay || !mouse) {
            return null
        }
        const local = overlay.toLocal(mouse.state().position as any)
        return overlay.worldLocation(local.x, local.y)
    }

    pulseDamage = (): void => {
        this.redout.alpha = 0.3
        this.redout.visible = true
        new Fader(this.redout, {
            fadeInterval: 25,
            releaseCallback: () => {
                this.redout.visible = false
            },
        }).update()
    }

    showWorldMap = (map: WorldMapMeta) => {
        const worldMap = new WorldMap(this, map, coordinate => {
            if (this.logic.runLevel === "dev") {
                this.logic.playerController.client.worldMapClicked(coordinate)
                worldMap.close()
            }
        })
        worldMap.toggle()
    }

    private isControlKeyPressed = (): boolean => {
        return (
            this.logic.clientRenderer.keyboard().state().get("Control") ||
            this.logic.clientRenderer.keyboard().state().get("ControlLeft") ||
            this.logic.clientRenderer.keyboard().state().get("ControlRight")
        )
    }

    // True when one of the player's item screens (inventory, crafting, or trading) is the open
    // modal. Under the modern renderer all three are a single ModernInventoryDialog; under the
    // classic renderer crafting/trading are still recognizable from the opening message.
    // Public so the world-click handler can suppress attacks while a screen is up.
    public isItemScreenOpen = (): boolean => {
        const modal = this.currentModal
        if (!modal) {
            return false
        }
        if (modal instanceof ModernInventoryDialog) {
            return true
        }
        return isCraftingMessage(modal.message) || isTradeMessage(modal.message)
    }

    // Called both by the global Control-key path (no args → broadcast wheel) and by the server's
    // OPEN_COMMAND_WHEEL reply (opts → wheel bound to one NPC, locked to its role).
    public showCommandWheel = (opts?: { targetEntityId: string; role: CommandWheelRole }) => {
        // The command wheel is only available in rooms that opt in (ember rooms), signalled by the
        // per-room enableCommandWheel flag that rides to the client in WorldState.visibility.
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        // Don't let the player summon the wheel on top of an inventory/crafting/trading screen.
        // The server-driven per-NPC wheel (opts present) is intentional and stays allowed.
        if (!opts && this.isItemScreenOpen()) {
            return
        }
        // A wheel going up replaces whatever the pointer was being used for: an armed location pick
        // has no press left to answer it, and its reticle would sit under the wheel unexplained.
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        // Whatever this wheel is about, it is no longer the roster card's.
        this.releaseRosterWheel()
        if (opts) {
            this.commandWheel.bindToEntity(opts.targetEntityId, opts.role)
            // A per-entity wheel opens directly over its NPC instead of screen-center, and stays
            // there: updateCommandWheelTether draws the line to the NPC as it walks off.
            this.positionCommandWheelOverEntity(opts.targetEntityId)
        } else {
            this.commandWheel.clearTarget()
            this.commandWheel.clearSelection() // Clear any previous selection
            // The global (Control-key) wheel returns to its screen-center home in case a prior
            // per-entity wheel moved it.
            this.commandWheel.position.copyFrom(this.commandWheelHome)
        }
        this.commandWheel.fadeIn()

        // Prevent context menu when command wheel is visible
        this.preventContextMenu()
        if (opts) this.learnedEntityMenu(opts.targetEntityId)
    }

    // Open one camp member's command wheel from their ROSTER CARD.
    //
    // Unlike the per-NPC wheel above, this one is not put over its entity: a member you reach through
    // the roster is usually the one you can't see, so it takes the HUD slot directly above the strip
    // (the same slot the follow-cam PiP and the minimap share) and a connector line carries which
    // card it belongs to. For the same reason it does NOT close when its NPC leaves the viewport.
    public openRosterCommandWheel = (entityId: EntityId, role: CommandWheelRole) => {
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        if (this.isItemScreenOpen()) {
            return
        }
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        this.commandWheelRosterId = entityId
        this.commandWheel.bindToEntity(entityId, role)
        this.positionRosterCommandWheel()
        this.commandWheel.fadeIn()
        // The wheel has taken the slot the minimap was holding.
        this.positionEmberMinimap()
        this.preventContextMenu()
        this.learnedEntityMenu(entityId)
    }

    // Park a roster-opened wheel in the slot above the strip, clamped so no part of the disc -- glow
    // included -- can leave the screen, however few cards the strip is down to.
    private positionRosterCommandWheel = () => {
        // positionEmberRoster runs during init before the wheel exists -- guard against that.
        if (!this.commandWheelRosterId || !this.commandWheel) {
            return
        }
        const span = 2 * (this.commandWheel.getRadius() + WHEEL_GLOW_REACH)
        const slot = this.rosterSlotAnchor(span, span)
        const at = this.clampToScreen(slot, span, span)
        // rosterSlotAnchor and clampToScreen work in the HUD container's frame; the wheel is a direct
        // child of the stage, and is centre-anchored rather than top-left.
        this.commandWheel.position.set(
            this.container.x + at.x + span / 2,
            this.container.y + at.y + span / 2,
        )
        this.updateRosterWheelConnector()
    }

    // The line from a roster-opened wheel down to the card that opened it, in the wheel's accent (the
    // member's role colour). Drawn UNDER the strip -- see where rosterWheelConnector is built -- so an
    // offscreen-speech bubble, and every other piece of HUD, reads over the top of it.
    private updateRosterWheelConnector = () => {
        const g = this.rosterWheelConnector
        g.clear()
        const anchor = this.commandWheelRosterId
            ? this.emberRoster.cardAnchor(this.commandWheelRosterId, "panel")
            : null
        if (!anchor || !this.commandWheel.isOpen()) {
            g.visible = false
            return
        }
        // Both ends in the HUD container's frame (the wheel lives on the stage, one level up). The
        // card end stops a dot's width clear of the panel: the line runs UNDER the strip, so a dot
        // sitting on the panel's edge would be half buried by it.
        const toX = anchor.x
        const toY = anchor.y - WHEEL_TETHER_DOT_RADIUS
        const fromCenterX = this.commandWheel.x - this.container.x
        const fromCenterY = this.commandWheel.y - this.container.y
        const dx = toX - fromCenterX
        const dy = toY - fromCenterY
        const distance = Math.hypot(dx, dy)
        const rim = this.commandWheel.getRadius() + WHEEL_TETHER_RIM_GAP
        if (distance < rim + WHEEL_TETHER_MIN_LENGTH) {
            g.visible = false
            return
        }
        g.visible = true
        g.alpha = this.commandWheel.alpha * WHEEL_TETHER_ALPHA
        g.lineStyle({ width: WHEEL_TETHER_WIDTH, color: anchor.color, alpha: 1, cap: LINE_CAP.ROUND })
        g.moveTo(fromCenterX + (dx / distance) * rim, fromCenterY + (dy / distance) * rim)
        g.lineTo(toX, toY)
        g.beginFill(anchor.color, 1)
        g.drawCircle(toX, toY, WHEEL_TETHER_DOT_RADIUS)
        g.endFill()
    }

    private clearRosterWheelConnector = () => {
        this.rosterWheelConnector.clear()
        this.rosterWheelConnector.visible = false
    }

    // Outline the roster's selected member out in the world (undefined takes it off). Routed through
    // the HUD rather than called from the roster widget directly, which knows only about this class.
    public highlightEmberMember = (entityId?: EntityId) => {
        this.logic.clientRenderer.entity().setRosterHighlight(entityId)
    }

    // Put away whatever the roster's active card opened -- the follow-cam PiP or that member's
    // command wheel -- and clear the card's highlight. Called by the roster before it acts on a tap,
    // so only one card is ever live and tapping the active card is a toggle.
    public closeRosterSelection = () => {
        this.dismissFollowCam()
        if (this.commandWheelRosterId) {
            this.hideCommandWheel()
        }
        if (this.rolePickerRosterId) {
            this.hideRolePicker()
        }
    }

    // Open a VISITOR'S OWN VERBS from their roster card: the same wheel a tap on the person would
    // give you ("Give Food", and "Talk" once they are fed), parked in the HUD slot above the strip
    // with a connector down to the card -- because the entire reason the player is reading a card
    // is that they cannot see who it belongs to.
    //
    // It borrows the roster command wheel's plumbing wholesale (`commandWheelRosterId` is what pins
    // it to the slot, draws the connector, and keeps it alive while its NPC is off screen); what
    // `rosterInteractionId` adds is that a selection goes out on the INTERACTION channel instead of
    // the squad one, and that the slices know how far away the player is standing.
    public openRosterPlayerWheel = (entityId: EntityId, name?: string) => {
        if (this.isItemScreenOpen()) return
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        this.commandWheelRosterId = entityId
        this.rosterInteractionName = name
        this.commandWheel.bindToInteractions(entityId, [
            { name: WATCH_SLICE, displayName: "Watch" },
            { name: "ember:kick", displayName: "Kick" },
            { name: "ember:play_mode", displayName: "Play Mode" },
        ], "👤")
        this.positionRosterCommandWheel()
        this.commandWheel.fadeIn()
        this.positionEmberMinimap()
        this.preventContextMenu()
    }

    public openRosterInteractionWheel = (entityId: EntityId, name?: string) => {
        if (this.isItemScreenOpen()) {
            return
        }
        this.logic.playerController?.cancelLocationPick()
        this.clearCommandWheelTether()
        this.commandWheelRosterId = entityId
        this.rosterInteractionId = entityId
        this.rosterInteractionName = name
        this.bindRosterInteractions(entityId)
        this.positionRosterCommandWheel()
        this.commandWheel.fadeIn()
        // The wheel has taken the slot the minimap was holding.
        this.positionEmberMinimap()
        this.preventContextMenu()
    }

    // Is the player close enough to hand something over? See INTERACTION_REACH: this is the "can
    // you see them" rule a world tap always enforced by accident, written down so a card can
    // enforce it too.
    private withinInteractionReach = (entityId: EntityId): boolean => {
        const me = this.logic.playerEntity
        const them = this.logic.getEntity(entityId)
        if (!me?.location || !them?.location) {
            return false
        }
        const dx = me.location.x - them.location.x
        const dy = me.location.y - them.location.y
        const reach = INTERACTION_REACH_TILES * TileSize
        return dx * dx + dy * dy <= reach * reach
    }

    // Write the slices for a roster-opened interaction wheel. A verb that needs the player to be
    // there says so in its own label rather than being hidden: a card that silently dropped "Give
    // Food" would teach the player that a visitor wants nothing, which is the opposite of the
    // lesson. WATCH is appended by the HUD, not the entity -- it is how one tap still reaches the
    // follow cam now that the card's tap belongs to the verbs.
    private bindRosterInteractions = (entityId: EntityId) => {
        const descriptors = this.logic.clientRenderer.entity().getEntityInteractions(entityId) || []
        const inReach = this.withinInteractionReach(entityId)
        this.rosterInteractionInReach = inReach
        const commands = descriptors.map(descriptor => ({
            name: descriptor.type as string,
            displayName:
                interactionNeedsReach(descriptor.type) && !inReach
                    ? `${interactionLabel(descriptor)} (too far)`
                    : interactionLabel(descriptor),
        }))
        commands.push({ name: WATCH_SLICE, displayName: "Watch" })
        this.commandWheel.bindToInteractions(entityId, commands)
    }

    // Called every frame while a roster interaction wheel is up: the player can walk into (or out
    // of) reach with it open, and a label that went stale would be a lie about the only thing the
    // wheel is being asked.
    private refreshRosterInteractionReach = () => {
        if (!this.rosterInteractionId) {
            return
        }
        if (this.withinInteractionReach(this.rosterInteractionId) === this.rosterInteractionInReach) {
            return
        }
        this.bindRosterInteractions(this.rosterInteractionId)
    }

    // Open the wheel over an entity to choose between the interactions it offers. Unlike
    // showCommandWheel this is NOT gated on the ember-room enableCommandWheel flag: chests, campfires
    // and denizens exist everywhere, and their interactions are how a player opens them now that the
    // hover icons are gone. Positioning, dismissal (Escape, Control, selection) and the fade are
    // shared with the command wheel.
    public showInteractionWheel = (entityId: EntityId, interactions: InteractionDescriptor[]) => {
        if (!interactions?.length) {
            return
        }
        // Don't put a wheel up underneath an inventory/crafting/trading screen.
        if (this.isItemScreenOpen()) {
            return
        }
        this.logic.playerController?.cancelLocationPick()
        this.releaseRosterWheel()
        this.commandWheel.bindToInteractions(
            entityId,
            interactions.map(next => ({ name: next.type, displayName: interactionLabel(next) })),
        )
        this.clearCommandWheelTether()
        if (!this.positionCommandWheelOverEntity(entityId)) {
            this.commandWheel.position.copyFrom(this.commandWheelHome)
        }
        this.commandWheel.fadeIn()
        this.preventContextMenu()
        this.learnedEntityMenu(entityId)
    }

    // The given entity's center in stage-local coordinates (the frame the command wheel / role
    // picker live in, as direct children of the stage). Null when the entity isn't available yet.
    private entityStageLocal = (entityId: string): { x: number; y: number } | null => {
        const entity = this.logic.getEntity(entityId)
        if (!entity) {
            return null
        }
        const overlay = this.logic.clientRenderer.map().overlay()
        // computeLocation gives the entity's center in the overlay surface's local frame; convert
        // that into stage-local coordinates.
        const center = overlay.computeLocation(entity.location.x, entity.location.y)
        return this.stage.toLocal(center, overlay)
    }

    // Move the wheel so its center sits over the given entity. Returns false when the entity (or its
    // overlay frame) isn't available yet. Called once, at open: the wheel then stays put (a wheel
    // that chased a walking NPC was impossible to aim at), and the tether line carries the link.
    private positionCommandWheelOverEntity = (entityId: string): boolean => {
        const local = this.entityStageLocal(entityId)
        if (!local) {
            return false
        }
        this.commandWheel.position.set(local.x, local.y)
        return true
    }

    // Per-frame upkeep for a per-entity wheel: drop it once its target is gone or has left the
    // viewport (there is nothing left on screen to command), and otherwise redraw the tether from
    // the pinned wheel to the NPC. No-op for the global broadcast wheel (no bound target) and while
    // the wheel is closed. Called every frame by the renderer.
    public updateCommandWheelTether = () => {
        const entityId = this.commandWheel.isOpen() ? this.commandWheel.getTargetEntityId() : undefined
        if (!entityId) {
            this.clearCommandWheelTether()
            this.clearRosterWheelConnector()
            return
        }
        const entity = this.logic.getEntity(entityId)
        const gone = !entity || this.logic.isDestroyed(entityId)
        // A ROSTER-opened wheel is pinned to the HUD, not to the world: it is tied to a card rather
        // than to the NPC, and it must survive its member being off screen -- reaching somebody you
        // cannot see is what the roster is for. Only the member actually going closes it.
        if (this.commandWheelRosterId) {
            if (gone) {
                this.hideCommandWheel()
                return
            }
            this.refreshRosterInteractionReach()
            this.clearCommandWheelTether()
            this.updateRosterWheelConnector()
            return
        }
        if (gone || !this.logic.isWithinPlayerViewport(entity.location)) {
            this.hideCommandWheel()
            return
        }
        this.drawCommandWheelTether(entityId)
    }

    // A translucent line from the pinned wheel's rim to the commanded NPC's center, with a dot on
    // the NPC end. Drawn in the wheel's accent and multiplied by the wheel's own alpha so it fades
    // in and out with it. Suppressed while the NPC is still under the disc, where a stub of line
    // would only show as a nub poking out of the hub.
    private drawCommandWheelTether = (entityId: string) => {
        const g = this.commandWheelTether
        g.clear()
        const local = this.entityStageLocal(entityId)
        if (!local) {
            g.visible = false
            return
        }
        const dx = local.x - this.commandWheel.x
        const dy = local.y - this.commandWheel.y
        const distance = Math.hypot(dx, dy)
        const rim = this.commandWheel.getRadius() + WHEEL_TETHER_RIM_GAP
        if (distance < rim + WHEEL_TETHER_MIN_LENGTH) {
            g.visible = false
            return
        }
        const accent = this.commandWheel.getAccent()
        g.visible = true
        g.alpha = this.commandWheel.alpha * WHEEL_TETHER_ALPHA
        g.lineStyle({ width: WHEEL_TETHER_WIDTH, color: accent, alpha: 1, cap: LINE_CAP.ROUND })
        g.moveTo(this.commandWheel.x + (dx / distance) * rim, this.commandWheel.y + (dy / distance) * rim)
        g.lineTo(local.x, local.y)
        g.beginFill(accent, 1)
        g.drawCircle(local.x, local.y, WHEEL_TETHER_DOT_RADIUS)
        g.endFill()
    }

    private clearCommandWheelTether = () => {
        this.commandWheelTether.clear()
        this.commandWheelTether.visible = false
    }

    // Let go of a roster-opened wheel: drop its connector, un-highlight the card that opened it, and
    // hand the slot above the strip back to the minimap. Called wherever the wheel stops being that
    // card's -- it closing, or another kind of wheel taking it over.
    private releaseRosterWheel = () => {
        if (!this.commandWheelRosterId) {
            return
        }
        this.commandWheelRosterId = undefined
        this.rosterInteractionId = undefined
        this.rosterInteractionName = undefined
        this.clearRosterWheelConnector()
        this.emberRoster.clearSelection()
        this.positionEmberMinimap()
    }

    public hideCommandWheel = () => {
        this.releaseRosterWheel()
        this.commandWheel.fadeOut()
        this.commandWheel.clearTarget()
        this.commandWheel.clearSelection() // Clear any previous selection
        this.clearCommandWheelTether()

        // Restore normal context menu behavior
        this.restoreContextMenu()
    }

    public isCommandWheelVisible = () => {
        return this.commandWheel.isOpen()
    }

    // Server-driven: open the recruit role picker over a specific camp member so the player can assign
    // it a role. Gated to ember rooms via the same per-room visibility flag as the command wheel.
    //
    // `currentRole` arrives only with a "Change Role" order on an already-hired member, and narrows the
    // picker to the job that member is NOT doing.
    public showRolePicker = (entityId: string, currentRole?: RecruitRole, playMode?: EmberPlayMode) => {
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        // A picker asked for from a roster card is not about a spot on screen; it inherits the HUD slot
        // (and the lit card, and the connector) from the wheel that asked for it. Anything else -- a
        // hire at the fire, a per-entity wheel -- opens over the member, where the player is looking.
        if (this.rolePickerRosterId && this.rolePickerRosterId !== entityId) {
            this.rolePickerRosterId = undefined
        }
        const fromRoster = this.rolePickerRosterId === entityId
        if (playMode) this.rolePicker.bindPlayMode(entityId, playMode)
        else this.rolePicker.bindToEntity(entityId, currentRole)
        if (fromRoster) {
            this.rolePicker.setTailVisible(false)
            this.positionRolePickerInRosterSlot()
            this.emberRoster.holdSelection(entityId)
            // The picker has taken the slot the minimap was holding.
            this.positionEmberMinimap()
        } else {
            this.positionRolePickerOverEntity(entityId)
        }
        this.rolePicker.fadeIn()
        this.preventContextMenu()
    }

    // Park a roster-raised picker in the slot above the strip, clamped so none of it can leave the
    // screen -- the same slot, and the same clamp, the wheel that raised it was using.
    private positionRolePickerInRosterSlot = () => {
        if (!this.rolePickerRosterId || !this.rolePicker) {
            return
        }
        const width = this.rolePicker.getPanelWidth()
        const height = this.rolePicker.getPanelHeight()
        const at = this.clampToScreen(this.rosterSlotAnchor(width, height), width, height)
        // rosterSlotAnchor and clampToScreen work in the HUD container's frame; the picker is a direct
        // child of the stage.
        this.rolePicker.placeBodyTopLeftAt(this.container.x + at.x, this.container.y + at.y)
        this.updateRolePickerConnector()
    }

    // The line from a roster-raised picker down to the card that raised it, in that member's role
    // colour -- the same connector the roster command wheel draws, and drawn with the same Graphics,
    // because the wheel is always closed by the time the picker is up.
    private updateRolePickerConnector = () => {
        const g = this.rolePickerConnector
        g.clear()
        const anchor = this.rolePickerRosterId
            ? this.emberRoster.cardAnchor(this.rolePickerRosterId, "panel")
            : null
        if (!anchor || !this.rolePicker.isOpen()) {
            g.visible = false
            return
        }
        // Both ends in the HUD container's frame. The picker's own origin is its tail TIP, which sits
        // below the body -- the line starts from the body's bottom edge, where the tail would have been.
        const toX = anchor.x
        const toY = anchor.y - WHEEL_TETHER_DOT_RADIUS
        const fromX = this.rolePicker.x - this.container.x
        const fromY = this.rolePicker.y - this.container.y - this.rolePicker.getTailDrop()
        if (toY - fromY < WHEEL_TETHER_MIN_LENGTH) {
            g.visible = false
            return
        }
        g.visible = true
        g.alpha = this.rolePicker.alpha * WHEEL_TETHER_ALPHA
        g.lineStyle({ width: WHEEL_TETHER_WIDTH, color: anchor.color, alpha: 1, cap: LINE_CAP.ROUND })
        g.moveTo(fromX, fromY + WHEEL_TETHER_RIM_GAP)
        g.lineTo(toX, toY)
        g.beginFill(anchor.color, 1)
        g.drawCircle(toX, toY, WHEEL_TETHER_DOT_RADIUS)
        g.endFill()
    }

    // Position the picker so its tail tip sits at the recruit's center (the panel floats above).
    // The picker is glued to its recruit, so the tail is true for exactly as long as there is
    // somebody to glue it to: once the recruit is gone from this client the panel is left standing
    // where it was, and the pointer comes off rather than aim at empty ground.
    private positionRolePickerOverEntity = (entityId: string): boolean => {
        const local = this.logic.isDestroyed(entityId) ? null : this.entityStageLocal(entityId)
        if (!local) {
            this.rolePicker.setTailVisible(false)
            return false
        }
        this.rolePicker.position.set(local.x, local.y)
        this.rolePicker.setTailVisible(true)
        return true
    }

    // Keep the picker glued over its member as the entity (or camera) moves. No-op while closed.
    // Called every frame by the renderer.
    public updateRolePickerPosition = () => {
        if (!this.rolePicker.isOpen()) {
            return
        }
        const entityId = this.rolePicker.getTargetEntityId()
        if (!entityId) {
            return
        }
        // A roster-raised picker is pinned to the HUD, not to the world: it is tied to a CARD, and must
        // survive its member being off screen -- reaching somebody you cannot see is what the roster is
        // for. Only the member actually going takes it down.
        if (this.rolePickerRosterId === entityId) {
            if (this.logic.isDestroyed(entityId) || !this.logic.getEntity(entityId)) {
                this.hideRolePicker()
                return
            }
            this.positionRolePickerInRosterSlot()
            return
        }
        this.positionRolePickerOverEntity(entityId)
    }

    public hideRolePicker = () => {
        this.emberInviteModal?.close()
        const wasRoster = this.rolePickerRosterId
        this.rolePickerRosterId = undefined
        this.rolePicker.fadeOut()
        this.rolePicker.clearTarget()
        if (wasRoster) {
            // Hand the card, the connector and the slot back the way a roster wheel does when it closes.
            this.rolePickerConnector.clear()
            this.rolePickerConnector.visible = false
            this.emberRoster.clearSelection()
            this.positionEmberMinimap()
        }
        this.restoreContextMenu()
    }

    // Server-driven: put the name field up over one camp member, started on `name`. Gated to ember
    // rooms by the same per-room visibility flag as the command wheel and the role picker.
    public showNamePrompt = (entityId: EntityId, name?: string) => {
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        this.namePrompt.bindToEntity(entityId, name)
        if (!this.positionNamePromptOverEntity(entityId)) {
            // The entity isn't on this client yet (a hire names the recruit in the same breath as
            // swapping its script). Nothing to pin to, so there is nothing sensible to show.
            this.namePrompt.clearTarget()
            return
        }
        this.namePrompt.fadeIn()
        // The field now owns the keyboard: see Keyboard.suppressTyping. Released in hideNamePrompt,
        // and Escape still gets through, which is what guarantees there is always a way out.
        Keyboard.suppressTyping(true)
        this.preventContextMenu()
    }

    // Position the prompt so its tail tip sits at the member's center (the panel floats above).
    // Called once, at open: unlike the role picker the prompt does NOT follow its entity, because a
    // field that slides out from under the cursor while being typed into is far worse than one
    // left standing where the player opened it. The tail is what pays for that -- see
    // updateNamePrompt.
    private positionNamePromptOverEntity = (entityId: EntityId): boolean => {
        const local = this.entityStageLocal(entityId)
        if (!local) {
            return false
        }
        this.namePrompt.position.set(local.x, local.y)
        this.namePrompt.setTailVisible(true)
        return true
    }

    // Per-frame upkeep: drop the prompt once the entity being named is gone -- there is nobody left
    // to name. Deliberately NOT closed when that entity merely walks off screen: a member can wander
    // out of view while their name is half typed, and losing the field then would lose the name.
    //
    // What does go is the tail. The panel is pinned where it opened, so the member (or the camera)
    // only has to move for the pointer to be aiming at bare ground, which reads as the prompt being
    // about whatever is standing there now. Checked every frame, and it comes back if they walk
    // back under it.
    public updateNamePrompt = () => {
        if (!this.namePrompt.isOpen()) {
            return
        }
        const entityId = this.namePrompt.getTargetEntityId()
        if (!entityId) {
            return
        }
        if (!this.logic.getEntity(entityId) || this.logic.isDestroyed(entityId)) {
            this.hideNamePrompt()
            return
        }
        const local = this.entityStageLocal(entityId)
        const onTip =
            !!local && Math.hypot(local.x - this.namePrompt.x, local.y - this.namePrompt.y) <= PANEL_TAIL_SLACK
        this.namePrompt.setTailVisible(onTip)
    }

    public hideNamePrompt = () => {
        this.namePrompt.fadeOut()
        this.namePrompt.clearTarget()
        Keyboard.suppressTyping(false)
        this.restoreContextMenu()
    }

    public isNamePromptVisible = () => {
        return this.namePrompt.isOpen()
    }

    // The run is over: rebuild the scorecard around this payload and fade it in over the camp.
    public showRunSummary = (summary: EmberRunSummary) => {
        if (!summary) {
            return
        }
        // Nothing else should be competing for the screen once the run has ended.
        this.hideCommandWheel()
        this.hideRolePicker()
        this.hideNamePrompt()
        this.hideInvasionPicker()
        this.dismissFollowCam()
        // Prefer the synced entity (it carries the current appearance/inventory the paperdoll needs)
        // and fall back to the local player stub.
        const player = this.logic.getEntity(this.logic.playerEntity?.entityId) || this.logic.playerEntity
        this.runSummary.setSummary(summary, player)
        this.runSummary.fadeIn()
    }

    public hideRunSummary = () => {
        this.runSummary.fadeOut()
        // The picker is opened FROM the scorecard and is meaningless without it: the panel going
        // away means the player took one of its other ways on, and a chooser left floating over a
        // room they have already left would still send a `join`.
        this.hideInvasionPicker()
    }

    public isRunSummaryVisible = () => {
        return this.runSummary?.isOpen() === true
    }

    // Server-driven: the newest invasion offer (Protocol.type_updateInvasionOffer). An interval
    // feed, ~2s, NOT a reply to a press -- which is the point: the scorecard's third button lights
    // up by itself when a camp becomes available, and an open picker gains and loses cards while
    // it is being read.
    //
    // `null` forgets the offer and takes the picker down. An EMPTY list does neither: it is a real
    // answer ("no camp is open to the dead") and the normal steady state on a quiet server, so the
    // button stays, inert and saying so, and an open picker shows its empty state rather than
    // vanishing under the player.
    updateInvasionOffer = (candidates: EmberInvasionCandidate[] | null) => {
        if (!candidates) {
            this.invasionCandidates = undefined
            this.runSummary?.setInvasionOffer(undefined)
            this.hideInvasionPicker()
            return
        }
        this.invasionCandidates = candidates
        this.runSummary?.setInvasionOffer(candidates)
        if (this.invasionPicker?.isOpen()) {
            this.invasionPicker.setCandidates(candidates)
        }
    }

    // Open the chooser on what the client already holds. No round trip: see the scorecard's
    // onInvade. Refuses to open on nothing, because the inert button should not have been pressable
    // in the first place (runSummaryButtons) and a picker opening onto its own empty state would
    // read as a broken press rather than as an honest answer.
    public showInvasionPicker = () => {
        if (!this.invasionCandidates || this.invasionCandidates.length < 1) {
            return
        }
        this.invasionPicker.open(this.invasionCandidates)
    }

    public hideInvasionPicker = () => {
        if (this.invasionPicker?.isOpen()) {
            this.invasionPicker.close()
        }
    }

    public isInvasionPickerVisible = () => {
        return this.invasionPicker?.isOpen() === true
    }

    // Backing out of the picker, from any of its three ways out: the footer button, a press on its
    // own scrim, and Escape (the house convention for every other modal -- see Player's Escape
    // chain). All three go through here rather than each pairing the message with the close
    // themselves, which is how the keyboard path and the pointer path drift apart.
    public cancelInvasionPicker = () => {
        if (!this.isInvasionPickerVisible()) {
            return
        }
        this.logic.playerController?.client?.sendEmberInvasionCommand("cancel")
        this.hideInvasionPicker()
    }

    // Whether this player is currently wearing one of another camp's attackers (Message.invading).
    // Taken from the server's verdict on every message, like the downed flag, so the way out cannot
    // outlive the body -- or go missing while it lives.
    //
    // It is also what takes the SCORECARD down, and the timing is the point. The other two ways off
    // that panel dismiss it as they send, because they cannot fail; a `join` can -- the camp may
    // have closed, or filled, between the feed and the press -- and a panel dismissed optimistically
    // on a refused join would leave a still-fallen player with no way out at all until the camp's
    // own 3-minute net fired. So the panel waits for the server to say the visit really started.
    public setInvading = (invading: boolean) => {
        this.letGoButton?.setInvading(invading)
        if (invading && !this.invading && this.isRunSummaryVisible()) {
            this.hideRunSummary()
        }
        // Each end of a visit, announced once. Both ends are this ONE verdict's transitions, which
        // is why neither needs a message of its own: the server already tells the client whether it
        // is wearing somebody else's body, every message, for its own entity.
        //
        // A visit that ends by dying also builds the player a fresh camp, so the "returned" splash
        // goes up alongside a room load -- hence its zIndex above everything, and hence nothing in
        // the room-change path takes it down. It waits to be read.
        const splash = splashForInvadingChange(this.invading, invading)
        if (splash) {
            this.showPossessionSplash(splash)
        }
        this.invading = invading
    }

    public showPossessionSplash = (kind: PossessionSplashKind) => {
        this.possessionSplash?.open(kind)
    }

    public hidePossessionSplash = () => {
        if (this.possessionSplash?.isOpen()) {
            this.possessionSplash.close()
        }
    }

    public isPossessionSplashVisible = () => this.possessionSplash?.isOpen() === true

    public isRolePickerVisible = () => {
        return this.rolePicker.isOpen() || this.emberInviteModal?.isOpen() === true
    }

    // Server-driven: put up the first-run tutorial invitation (or take it down again when the
    // server passes null -- e.g. the player answered on another client, or the camp moved on).
    // Gated to ember rooms by the same per-room visibility flag as the command wheel and the role
    // picker, so a stray payload can never park a bar over a normal room.
    public showTutorialPrompt = (prompt: EmberTutorialPrompt | null) => {
        if (!prompt) {
            this.hideTutorialPrompt()
            return
        }
        if (!this.logic.clientRenderer.map().getVisibility()?.enableCommandWheel) {
            return
        }
        this.tutorialPrompt.setPrompt(prompt)
        // The bar sizes itself to the server's wording, so re-anchor it to the bottom of the
        // screen AFTER it has been rebuilt -- otherwise a taller prompt hangs off the edge.
        this.anchorTutorialPrompt()
        this.tutorialPrompt.fadeIn()
    }

    // Keep the invitation a fixed distance off the bottom edge, whatever height its copy needs.
    private anchorTutorialPrompt = () => {
        this.tutorialPrompt.y = ScreenDimensions.h - this.tutorialPrompt.stageHeight() - 18
    }

    public hideTutorialPrompt = () => {
        this.tutorialPrompt.fadeOut()
    }

    public isTutorialPromptVisible = () => {
        return this.tutorialPrompt.isOpen()
    }

    private preventContextMenu = () => {
        // Add document-level context menu prevention
        document.addEventListener("contextmenu", this.onContextMenu, true)
    }

    private restoreContextMenu = () => {
        // Remove document-level context menu prevention
        document.removeEventListener("contextmenu", this.onContextMenu, true)
    }

    private onContextMenu = (event: Event) => {
        // Only prevent context menu if command wheel is open
        if (this.commandWheel.isOpen()) {
            event.preventDefault()
            event.stopPropagation()
        }
    }
}
